import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { MeshGradient } from '@paper-design/shaders-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { useAuth } from '../contexts/AuthContext';
import HeroDither from '../components/shared/HeroDither';
import Reveal from '../components/shared/Reveal';
import { TextAnimate } from '../components/magicui/text-animate';
import { CustomersSection } from '@/components/ui/customers-section';
import { DiaText } from '@/components/ui/dia-text';
import { NumberTicker } from '@/components/ui/number-ticker';
import HoverPlayCard from '@/components/ui/hover-play-card';
// Lazy: it's the very last thing on the page — no one sees it without
// scrolling through the whole Homepage first, so its chunk loads in the
// background instead of blocking the initial bundle.
const RuixenGradientFooter = lazy(() =>
  import('@/components/ui/ruixen-gradient-footer').then((m) => ({ default: m.RuixenGradientFooter }))
);
import { LEGAL_ES } from '../constants/legalContent';


const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className={`nx-faq-item${open ? ' nx-faq-item--open' : ''}`} onClick={() => setOpen(o => !o)}>
      <div className="nx-faq-q">
        <span>{question}</span>
        <div className="nx-faq-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" className="nx-faq-plus"/>
            <path d="M5 12h14" className="nx-faq-minus"/>
          </svg>
        </div>
      </div>
      {open && <p className="nx-faq-a">{answer}</p>}
    </div>
  );
};

/* ─── LANGUAGE SWITCHER ──────────────────────────────────── */
const LANGS = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'pt', label: 'Português', short: 'PT' },
];

const LangSwitcher = ({ lang, setLang, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className={`nx-lang-dropdown ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="nx-lang-btn"
        aria-label="Cambiar idioma"
        aria-expanded={open}
      >
        {current.short}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="nx-lang-menu">
          {LANGS.map((l) => (
            <button
              key={l.code}
              className={`nx-lang-menu-item${l.code === lang ? ' nx-lang-menu-item--active' : ''}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── ICONS ──────────────────────────────────────────────── */
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const IconChevronDown = ({ className }) => (
  <svg className={className} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);
const IconCheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7"/>
  </svg>
);
const IconLock = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
  </svg>
);
const IconShieldCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/>
  </svg>
);
const IconBackup = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 4v5h-5"/>
  </svg>
);
const IconPrivacy = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3"/><path d="M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7"/><path d="M3.5 3.5l17 17"/>
  </svg>
);
const SECURITY_ICONS = [<IconLock/>, <IconShieldCheck/>, <IconBackup/>, <IconPrivacy/>];
const IconCardOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="14" height="10" rx="2"/><path d="M3.5 3.5l17 17"/>
  </svg>
);
const IconDbOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="6" rx="7" ry="3"/><path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6"/><path d="M3.5 3.5l17 17"/>
  </svg>
);
const IconBellOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M3.5 3.5l17 17"/>
  </svg>
);
const IconStopCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/>
  </svg>
);
const PROBLEM_ICONS = [<IconCardOff/>, <IconDbOff/>, <IconBellOff/>, <IconStopCircle/>];
const IconScanCard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7V5a1 1 0 011-1h2M4 17v2a1 1 0 001 1h2M20 7V5a1 1 0 00-1-1h-2M20 17v2a1 1 0 01-1 1h-2"/>
    <rect x="8" y="7" width="8" height="10" rx="1.5"/>
    <circle cx="12" cy="10.3" r="1.4"/>
    <path d="M9.6 15.2c.45-1.35 1.35-1.8 2.4-1.8s1.95.45 2.4 1.8"/>
  </svg>
);
const IconChecklist = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6h10M9 12h10M9 18h6"/>
    <path d="M4.5 5.5l1 1 2-2"/>
    <path d="M4.5 11.5l1 1 2-2"/>
    <path d="M4.5 17.5l1 1 2-2"/>
  </svg>
);
const IconCrmSync = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17.5A4.5 4.5 0 017 8.6a5.5 5.5 0 0110.6-1.1A4 4 0 0117 17.5H7z"/>
    <path d="M9.5 14a2.5 2.5 0 004.5.7M9.5 14l-.8 1.8M9.5 14l1.8.5"/>
    <path d="M14.5 12.5a2.5 2.5 0 00-4.5-.7M14.5 12.5l.8-1.8M14.5 12.5l-1.8-.5"/>
  </svg>
);
const IconGlobeEdit = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="11" r="7"/>
    <path d="M3 11h14M10 4a10 10 0 010 14M10 4a10 10 0 000 14"/>
    <path d="M16.5 21l.8-2.8 4-4 2 2-4 4-2.8.8z"/>
  </svg>
);
const IconHandshake = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 13l4.5-4.5L9 11l3-3 3 3 2.5-2.5L22 13"/>
    <path d="M7 12l3 3.5a1.5 1.5 0 002.2-2M14.2 13.5a1.5 1.5 0 012.2 2L17 16"/>
  </svg>
);
const IconNetworkNodes = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2.2"/>
    <circle cx="4" cy="6" r="1.8"/>
    <circle cx="4" cy="18" r="1.8"/>
    <circle cx="20" cy="6" r="1.8"/>
    <circle cx="20" cy="18" r="1.8"/>
    <path d="M10.3 10.7L5.5 7M13.7 10.7L18.5 7M10.3 13.3L5.5 17M13.7 13.3L18.5 17"/>
  </svg>
);
const LEADCAPTURE_ICONS = [
  [<IconScanCard/>, <IconChecklist/>, <IconCrmSync/>],
  [<IconGlobeEdit/>, <IconHandshake/>, <IconNetworkNodes/>],
];

const GHCMeshBg = () => {
  const wrapRef = useRef(null);
  const [active, setActive] = useState(false);
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const speed = prefersReduced ? 0 : isMobile ? 0.12 : 0.3;
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setActive(e.isIntersecting), { rootMargin: '150px' });
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={wrapRef} className="nx-ghc-mesh" style={{position:'absolute',inset:0,width:'100%',height:'100%',zIndex:0,pointerEvents:'none'}}>
      <MeshGradient
        style={{position:'absolute',inset:0,width:'100%',height:'100%'}}
        colors={['#1a3f6f','#4f9eff','#93c5fd','#2563eb','#e0f2fe']}
        speed={active ? speed : 0}
        backgroundColor="#1a3f6f"
        minPixelRatio={1}
      />
      <MeshGradient
        style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:isMobile?0.22:0.42}}
        colors={['#1a3f6f','#ffffff','#f0f9ff','#bae6fd']}
        speed={active ? speed * 0.6 : 0}
        wireframe={true}
        backgroundColor="transparent"
        minPixelRatio={1}
      />
      <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.08)'}} />
    </div>
  );
};

const HorizonGlowBg = () => {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let active = true;
    const obs = new IntersectionObserver(([e]) => { active = e.isIntersecting; }, { rootMargin: '150px' });
    if (wrapRef.current) obs.observe(wrapRef.current);
    let raf;
    const start = performance.now();
    const paint = (now) => {
      const el = ref.current;
      if (el && active) {
        const t = prefersReduced ? 0 : (now - start) / 1000;
        const ph = t * 0.72;
        const amt = 0.62;
        const spin = ph;
        const domeScale = 1 + Math.sin(spin * 0.8) * amt * 0.07;
        const glowScale = 1 + Math.sin(spin * 0.8) * amt * 0.35;
        const domeW = (101 * domeScale).toFixed(3);
        const domeH = (49 * domeScale).toFixed(3);
        const bl1W = (42.42 * glowScale).toFixed(3);
        const bl1H = (18.62 * glowScale).toFixed(3);
        const bl2W = (53.53 * glowScale).toFixed(3);
        const bl2H = (28.42 * glowScale).toFixed(3);
        el.style.backgroundImage =
          `radial-gradient(${domeW}% ${domeH}% at 50% 116%, #1E3A8A 0%, #1D4ED8 23.8%, #2563EB 51.4%, #3B82F6 79%, rgba(59,130,246,0) 91%),` +
          `radial-gradient(${bl1W}% ${bl1H}% at 50% 116%, rgba(37,99,235,0.367) 0%, rgba(37,99,235,0.1428) 38%, rgba(37,99,235,0) 72%),` +
          `radial-gradient(${bl2W}% ${bl2H}% at 50% 116%, rgba(96,165,250,0.1326) 0%, rgba(96,165,250,0) 80%)`;
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    return () => { cancelAnimationFrame(raf); obs.disconnect(); };
  }, []);
  return <div ref={(n) => { ref.current = n; wrapRef.current = n; }} className="nx-feat-glow" aria-hidden="true"/>;
};

const ProductShowcase = ({ lang }) => {
  const t = TR[lang];
  return (
    <section className="nx-ghc-section" id="nosotros">
      <GHCMeshBg/>
      <div className="nx-container" style={{position:'relative',zIndex:1}}>
        <Reveal className="nx-ghc-header">
          <div className="nx-ghc-header-left">
            <h2 className="nx-ghc-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif"}}>
              {t.ghc.title1}<br/>
              {t.ghc.titlePre} <span className="nx-ghc-conectar">{t.ghc.titleWord}</span> {t.ghc.titlePost}
            </h2>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <div className="nx-gs-tray">
            <div className="nx-gs-shot">
              <img
                src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1785534475/Captura_de_pantalla_2026-07-31_a_la_s_5.33.50_p.m._aof33i.png"
                alt="Panel de CONTACT"
                loading="lazy"
                width="2816"
                height="1582"
              />
            </div>
            <div className="nx-gs-shot">
              <img
                src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1785534487/Captura_de_pantalla_2026-07-31_a_la_s_5.47.27_p.m._wqsdti.png"
                alt="Analytics de CONTACT"
                loading="lazy"
                width="2816"
                height="1588"
              />
            </div>
          </div>
          <p className="nx-gs-caption">{t.ghc.caption}</p>
        </Reveal>
      </div>
    </section>
  );
};

/* ─── LEAD CAPTURE + DIGITAL BUSINESS CARD SHOWCASE ─────────── */
const LeadCaptureShowcase = ({ lang }) => {
  const t = TR[lang].leadcapture;
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  return (
    <section className="nx-lc-section">
      <Reveal delay={140}>
        <div className="nx-container">
          <div className="nx-section-pre nx-section-pre--center">
            <h2 className="nx-section-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.title}</h2>
          </div>
          <div className="nx-lc-card">
            <div className="nx-lc-tabs">
              {t.tabs.map((tab, i) => (
                <button
                  key={i}
                  type="button"
                  className={`nx-lc-tab${active === i ? ' nx-lc-tab--active' : ''}`}
                  onClick={() => setActive(i)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="nx-lc-grid">
              {t.tabs[active].items.map((item, i) => (
                <div className="nx-lc-item" key={i}>
                  <span className="nx-lc-icon">{LEADCAPTURE_ICONS[active][i]}</span>
                  <h3 className="nx-lc-item-title">{item.title}</h3>
                  <p className="nx-lc-item-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="nx-lc-photo nx-lc-photo--bottom" style={{marginTop:20, marginBottom:0}}>
            <img
              src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788820252/ChatGPT_Image_7_sept_2026_07_30_30_p.m._wrz8ng.png"
              alt="Profesionales conectando en un evento"
              loading="lazy"
              width="1536"
              height="1024"
            />
          </div>
          <div style={{textAlign:'center', marginTop:28}}>
            <button onClick={() => navigate('/captura-de-leads')} className="nx-btn-outline">
              Ver más información <IconArrowRight/>
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

/* ─── API TERMINAL MOCKUP ────────────────────────────────── */
// Hand-tokenized, no runtime syntax highlighter — same approach already used
// in DeveloperPanel.js's CodeBlock, kept self-contained here since this is a
// marketing mockup, not the real dev panel.
const API_TERM_COLORS = { kw:'#0550AE', str:'#A31515', prop:'#0A3069', num:'#098658', muted:'#8B8B92', plain:'#1F2328' };
const API_TERM_TOKENS = [
  { t:'$ ', c:'muted' }, { t:'curl', c:'kw' }, { t:' https://contactnetwork.cl/api/public-api/v1/leads \\\n  ' },
  { t:'-H', c:'kw' }, { t:' ' }, { t:'"Authorization: YOUR_API_KEY"', c:'str' },
  { t:'\n\n' },
  { t:'{\n  ' }, { t:'"total"', c:'prop' }, { t:': ' }, { t:'128', c:'num' }, { t:',\n  ' },
  { t:'"leads"', c:'prop' }, { t:': [\n    ' },
  { t:'{ ' }, { t:'"id"', c:'prop' }, { t:': ' }, { t:'"a1b2c3d4"', c:'str' }, { t:', ' }, { t:'"nombre"', c:'prop' }, { t:': ' }, { t:'"Ana Torres"', c:'str' }, { t:', ' }, { t:'"telefono"', c:'prop' }, { t:': ' }, { t:'"+56911111111"', c:'str' }, { t:', ' }, { t:'"email"', c:'prop' }, { t:': ' }, { t:'"ana@empresa.cl"', c:'str' }, { t:', ' }, { t:'"estado"', c:'prop' }, { t:': ' }, { t:'"nuevo"', c:'str' }, { t:', ' }, { t:'"profile_id"', c:'prop' }, { t:': ' }, { t:'"perfil_123"', c:'str' }, { t:', ' }, { t:'"fecha_identificado"', c:'prop' }, { t:': ' }, { t:'"2026-08-20T14:32:00+00:00"', c:'str' }, { t:' },\n    ' },
  { t:'{ ' }, { t:'"id"', c:'prop' }, { t:': ' }, { t:'"e5f6g7h8"', c:'str' }, { t:', ' }, { t:'"nombre"', c:'prop' }, { t:': ' }, { t:'"Diego Rojas"', c:'str' }, { t:', ' }, { t:'"telefono"', c:'prop' }, { t:': ' }, { t:'"+56922222222"', c:'str' }, { t:', ' }, { t:'"email"', c:'prop' }, { t:': ' }, { t:'"diego@empresa.cl"', c:'str' }, { t:', ' }, { t:'"estado"', c:'prop' }, { t:': ' }, { t:'"contactado"', c:'str' }, { t:', ' }, { t:'"profile_id"', c:'prop' }, { t:': ' }, { t:'"perfil_123"', c:'str' }, { t:', ' }, { t:'"fecha_identificado"', c:'prop' }, { t:': ' }, { t:'"2026-08-19T09:10:00+00:00"', c:'str' }, { t:' }\n' },
  { t:'  ]\n}' },
];
// Split the flat token stream into per-line arrays (on embedded '\n') so we
// can render real line numbers instead of one long pre-wrapped blob.
const splitTokensIntoLines = (tokens) => {
  const lines = [[]];
  tokens.forEach(({ t, c }) => {
    t.split('\n').forEach((part, idx) => {
      if (idx > 0) lines.push([]);
      if (part.length) lines[lines.length - 1].push({ t: part, c });
    });
  });
  return lines;
};
const API_TERM_LINES = splitTokensIntoLines(API_TERM_TOKENS);
const IconCopySm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);
const ApiTerminalMockup = () => (
  <div className="nx-api-term">
    <div className="nx-api-term-bar">
      <span/>
      <span className="nx-api-term-tab">leads.sh</span>
      <span className="nx-api-term-copy"><IconCopySm/></span>
    </div>
    <div className="nx-api-term-body">
      {API_TERM_LINES.map((line, i) => (
        <div className="nx-api-term-line" key={i}>
          <span className="nx-api-term-linenum">{i + 1}</span>
          <span className="nx-api-term-code">
            {line.map((seg, j) => (
              <span key={j} style={{color: API_TERM_COLORS[seg.c || 'plain']}}>{seg.t}</span>
            ))}
            {i === API_TERM_LINES.length - 1 && <span className="nx-api-term-cursor"/>}
          </span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── ORBITING INTEGRATIONS (logos reales via Simple Icons) ───────────── */
const WebhookGlyph = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
    <circle cx="6" cy="17" r="3" stroke="#0071e3" strokeWidth="1.8"/>
    <circle cx="18" cy="7" r="3" stroke="#0071e3" strokeWidth="1.8"/>
    <circle cx="18" cy="17" r="3" stroke="#0071e3" strokeWidth="1.8"/>
    <path d="M9 17h6M15.5 9.6 10.8 15" stroke="#0071e3" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const OrbitBrandIcon = ({ color, d }) => (
  <svg viewBox="0 0 24 24" width="22" height="22"><path fill={color} d={d}/></svg>
);
const ORBIT_ICON_D = {
  sheets: 'M11.318 12.545H7.91v-1.909h3.41v1.91zM14.728 0v6h6l-6-6zm1.363 10.636h-3.41v1.91h3.41v-1.91zm0 3.273h-3.41v1.91h3.41v-1.91zM20.727 6.5v15.864c0 .904-.732 1.636-1.636 1.636H4.909a1.636 1.636 0 0 1-1.636-1.636V1.636C3.273.732 4.005 0 4.909 0h9.318v6.5h6.5zm-3.273 2.773H6.545v7.909h10.91v-7.91zm-6.136 4.636H7.91v1.91h3.41v-1.91z',
  hubspot: 'M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z',
  notion: 'M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z',
  slack: 'M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z',
  gmail: 'M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z',
  outlook: 'M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z',
  zapier: 'M4.157 0A4.151 4.151 0 0 0 0 4.161v15.678A4.151 4.151 0 0 0 4.157 24h15.682A4.152 4.152 0 0 0 24 19.839V4.161A4.152 4.152 0 0 0 19.839 0H4.157Zm10.61 8.761h.03a.577.577 0 0 1 .23.038.585.585 0 0 1 .201.124.63.63 0 0 1 .162.431.612.612 0 0 1-.162.435.58.58 0 0 1-.201.128.58.58 0 0 1-.23.042.529.529 0 0 1-.235-.042.585.585 0 0 1-.332-.328.559.559 0 0 1-.038-.235.613.613 0 0 1 .17-.431.59.59 0 0 1 .405-.162Zm2.853 1.572c.03.004.061.004.095.004.325-.011.646.064.937.219.238.144.431.355.552.609.128.279.189.582.185.888v.193a2 2 0 0 1 0 .219h-2.498c.003.227.075.45.204.642a.78.78 0 0 0 .646.265.714.714 0 0 0 .484-.136.642.642 0 0 0 .23-.318l.915.257a1.398 1.398 0 0 1-.28.537c-.14.159-.321.284-.521.355a2.234 2.234 0 0 1-.836.136 1.923 1.923 0 0 1-1.001-.245 1.618 1.618 0 0 1-.665-.703 2.221 2.221 0 0 1-.227-1.036 1.95 1.95 0 0 1 .48-1.398 1.9 1.9 0 0 1 1.3-.488Zm-9.607.023c.162.004.325.026.48.079.207.065.4.174.563.314.26.302.393.692.366 1.088v2.276H8.53l-.109-.711h-.065c-.064.163-.155.31-.272.439a1.122 1.122 0 0 1-.374.264 1.023 1.023 0 0 1-.453.083 1.334 1.334 0 0 1-.866-.264.965.965 0 0 1-.329-.801.993.993 0 0 1 .076-.431 1.02 1.02 0 0 1 .242-.363 1.478 1.478 0 0 1 1.043-.303h.952v-.181a.696.696 0 0 0-.136-.454.553.553 0 0 0-.438-.154.695.695 0 0 0-.378.086.48.48 0 0 0-.193.254l-.99-.144a1.26 1.26 0 0 1 .257-.563c.14-.174.321-.302.533-.378.261-.091.54-.136.82-.129.053-.003.106-.007.163-.007Zm4.384.007c.174 0 .347.038.506.114.182.083.34.211.458.374.257.423.377.911.351 1.406a2.53 2.53 0 0 1-.355 1.448 1.148 1.148 0 0 1-1.009.517c-.204 0-.401-.045-.582-.136a1.052 1.052 0 0 1-.48-.457 1.298 1.298 0 0 1-.114-.234h-.045l.004 1.784h-1.059v-4.713h.904l.117.805h.057c.068-.208.177-.401.328-.56a1.129 1.129 0 0 1 .843-.344h.076v-.004Zm7.559.084h.903l.113.805h.053a1.37 1.37 0 0 1 .235-.484.813.813 0 0 1 .313-.242.82.82 0 0 1 .39-.076h.234v1.051h-.401a.662.662 0 0 0-.313.008.623.623 0 0 0-.272.155.663.663 0 0 0-.174.26.683.683 0 0 0-.027.314v1.875h-1.054v-3.666Zm-17.515.003h3.262v.896L3.73 13.104l.034.113h1.973l.042.9H2.4v-.9l1.931-1.754-.045-.117H2.441v-.896Zm11.815 0h1.055v3.659h-1.055V10.45Zm3.443.684.019.016a.69.69 0 0 0-.351.045.756.756 0 0 0-.287.204c-.11.155-.174.336-.189.522h1.545c-.034-.526-.257-.787-.74-.787h.003Zm-5.718.163c-.026 0-.057 0-.083.004a.78.78 0 0 0-.31.053.746.746 0 0 0-.257.189 1.016 1.016 0 0 0-.204.695v.064c-.015.257.057.507.204.711a.634.634 0 0 0 .253.196.638.638 0 0 0 .314.061.644.644 0 0 0 .578-.265c.14-.223.204-.48.189-.74a1.216 1.216 0 0 0-.181-.711.677.677 0 0 0-.503-.257Zm-4.509 1.266a.464.464 0 0 0-.268.102.373.373 0 0 0-.114.276c0 .053.008.106.027.155a.375.375 0 0 0 .087.132.576.576 0 0 0 .397.11v.004a.863.863 0 0 0 .563-.182.573.573 0 0 0 .211-.457v-.14h-.903Z',
  make: 'M13.38 3.498c-.27 0-.511.19-.566.465L9.85 18.986a.578.578 0 0 0 .453.678l4.095.826a.58.58 0 0 0 .682-.455l2.963-15.021a.578.578 0 0 0-.453-.678l-4.096-.826a.589.589 0 0 0-.113-.012zm-5.876.098a.576.576 0 0 0-.516.318L.062 17.697a.575.575 0 0 0 .256.774l3.733 1.877a.578.578 0 0 0 .775-.258l6.926-13.781a.577.577 0 0 0-.256-.776L7.762 3.658a.571.571 0 0 0-.258-.062zm11.74.115a.576.576 0 0 0-.576.576v15.426c0 .318.258.578.576.578h4.178a.58.58 0 0 0 .578-.578V4.287a.578.578 0 0 0-.578-.576Z',
};
const ORBIT_RING_CFG = {
  1: { duration: 16, dir: 'cw' },
  2: { duration: 24, dir: 'ccw' },
  3: { duration: 34, dir: 'cw' },
};
const ORBIT_ITEMS = [
  { ring: 1, angle: 10, color: '#4A154B', icon: 'slack' },
  { ring: 1, angle: 130, color: '#EA4335', icon: 'gmail' },
  { ring: 1, angle: 250, color: '#000000', icon: 'notion' },
  { ring: 2, angle: 40, color: '#FF7A59', icon: 'hubspot' },
  { ring: 2, angle: 160, color: '#34A853', icon: 'sheets' },
  { ring: 2, angle: 280, color: '#FF4F00', icon: 'zapier' },
  { ring: 3, angle: 70, color: '#0078D4', icon: 'outlook' },
  { ring: 3, angle: 190, color: '#6D00CC', icon: 'make' },
  { ring: 3, angle: 310, icon: 'webhook' },
];
const OrbitingIntegrations = () => (
  <div className="nx-orbit-wrap">
    <div className="nx-orbit-core"><img src="/favicon.png" alt="" className="nx-orbit-core-mark" width="297" height="295"/></div>
    <div className="nx-orbit-ring nx-orbit-r1"/>
    <div className="nx-orbit-ring nx-orbit-r2"/>
    <div className="nx-orbit-ring nx-orbit-r3"/>
    {ORBIT_ITEMS.map((it, i) => {
      const cfg = ORBIT_RING_CFG[it.ring];
      const spin = cfg.dir === 'cw' ? 'nxOrbitCw' : 'nxOrbitCcw';
      const counter = cfg.dir === 'cw' ? 'nxOrbitCounterCw' : 'nxOrbitCounterCcw';
      return (
        <div
          key={i}
          className={`nx-orbit-spoke nx-orbit-r${it.ring}`}
          style={{ '--start-angle': `${it.angle}deg`, animation: `${spin} ${cfg.duration}s linear infinite` }}
        >
          <div
            className="nx-orbit-bubble"
            style={{ '--start-angle': `${it.angle}deg`, animation: `${counter} ${cfg.duration}s linear infinite` }}
          >
            {it.icon === 'webhook' ? <WebhookGlyph/> : <OrbitBrandIcon color={it.color} d={ORBIT_ICON_D[it.icon]}/>}
          </div>
        </div>
      );
    })}
  </div>
);

/* ─── CONTACT PROFILES (fan carousel) ───────────────────── */
const CONTACT_PROFILES = [
  {
    name: 'Sophie Martin',
    role: 'CEO', tag: 'Fintech', tagColor: '#2563EB',
    company: 'Lumière Capital',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    cityImg: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=400&q=80',
    socials: ['LinkedIn', 'Email', 'Instagram', 'Sitio Web'],
  },
  {
    name: 'Emma Rossi',
    role: 'Directora', tag: 'Moda', tagColor: '#DB2777',
    company: 'Studio Roma',
    avatar: 'https://randomuser.me/api/portraits/women/26.jpg',
    cityImg: 'https://images.unsplash.com/photo-1531572753322-ad063cecc140?w=400&q=80',
    socials: ['Instagram', 'LinkedIn', 'Email', 'Sitio Web'],
  },
  {
    name: 'Ana Santos',
    role: 'CMO', tag: 'Retail', tagColor: '#059669',
    company: 'Alma Brands',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    cityImg: 'https://images.unsplash.com/photo-1548707309-dcebeab9ea9b?w=400&q=80',
    socials: ['Instagram', 'LinkedIn', 'WhatsApp', 'Sitio Web'],
  },
  {
    name: 'Carlos Ruiz',
    role: 'Fundador', tag: 'PropTech', tagColor: '#D97706',
    company: 'Nómada Ventures',
    avatar: 'https://randomuser.me/api/portraits/men/54.jpg',
    cityImg: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&q=80',
    socials: ['LinkedIn', 'WhatsApp', 'Email', 'Sitio Web'],
  },
  {
    name: 'Marco Bernardi',
    role: 'VP Sales', tag: 'HealthTech', tagColor: '#0284C7',
    company: 'Medicore EU',
    avatar: 'https://randomuser.me/api/portraits/men/41.jpg',
    cityImg: 'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=400&q=80',
    socials: ['LinkedIn', 'Email', 'WhatsApp', 'Sitio Web'],
  },
];

const FAN3_PROFILES = [CONTACT_PROFILES[0], CONTACT_PROFILES[2], CONTACT_PROFILES[4]]; // Sophie, Ana, Marco

/* ─── STRAIGHT PROFILE CARDS (sin rotación, solo 3) ────────── */
const FAN3_ICONS = {
  LinkedIn: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/>
      <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
    </svg>
  ),
  Instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  ),
  WhatsApp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  Email: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  'Sitio Web': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
};
const FAN3_SAVE_ICON = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
  </svg>
);
const StraightProfileCard = ({ data }) => (
  <div className="nx-fan3-card" style={{ backgroundImage: `url(${data.cityImg})` }}>
    <div className="nx-fan3-overlay" />
    <div className="nx-fan3-content">
      <div className="nx-fan3-av-wrap">
        <img src={data.avatar} alt={data.name} className="nx-fan3-av" loading="lazy" width="128" height="128" />
        <div className="nx-fan3-nfc" />
      </div>
      <div className="nx-fan3-name">{data.name}</div>
      <div className="nx-fan3-role">
        {data.role} · <span style={{ color: data.tagColor, fontWeight: 600 }}>{data.tag}</span>
      </div>
      <div className="nx-fan3-co">{data.company}</div>
      <button className="nx-fan3-save">{FAN3_SAVE_ICON} Guardar contacto</button>
      {data.socials.map((s, i) => (
        <div key={i} className="nx-fan3-link">
          {FAN3_ICONS[s] || null}
          {s}
        </div>
      ))}
    </div>
  </div>
);
const StraightProfileCards = ({ cards }) => (
  <div className="nx-fan3-row">
    {cards.map((c, i) => <StraightProfileCard key={i} data={c} />)}
  </div>
);


/* ─── METEORS ────────────────────────────────────────────── */
const Meteors = ({ number = 16 }) => {
  const meteors = Array.from({ length: number }, (_, i) => ({
    id: i,
    left: Math.floor(Math.random() * 800 - 400),
    delay: (Math.random() * 0.8 + 0.2).toFixed(2),
    duration: Math.floor(Math.random() * 8 + 4),
  }));
  return (
    <>
      {meteors.map(m => (
        <span
          key={m.id}
          className="nx-meteor"
          style={{ top: '-5px', left: m.left + 'px', animationDelay: m.delay + 's', animationDuration: m.duration + 's' }}
        />
      ))}
    </>
  );
};

/* ─── TRANSLATIONS (ES / EN) ─────────────────────────────── */
const TR = {
  es: {
    nav: { features:'Características', plans:'Planes', solution:'Solución', login:'Iniciar sesión', buy:'Comprar', access:'Acceder', quote:'Cotizar', myProfile:'Mi perfil', dashboard:'Dashboard', settings:'Configuración', logout:'Cerrar sesión' },
    hero: { trust:'150+ empresas activas · 2.500+ profesionales', connectPre:'Comparte tu', touch:'perfil', measurePre:'Mide cada', opportunity:'oportunidad', sub:'Transforma tus conexiones profesionales en resultados concretos.', cta:'Comenzar', trustLabel:'Confían en nosotros' },
    feat: {
      title1:'Convierte cada contacto', title2:'en una oportunidad.',
      sub:'Herramientas diseñadas para profesionales y equipos que miden cada contacto.',
      cta:'Solicitar cotización',
      cards:[
        { headline:'Tu identidad profesional, disponible en un toque.', body:'Un solo tap comparte toda tu información. Sin apps adicionales. Actualiza en cualquier momento y tus contactos verán siempre la versión vigente.', metricLabel:'más conexiones registradas' },
        { headline:'Decisiones basadas en datos, en tiempo real.', body:'Dashboard ejecutivo con métricas de visitas, conversión y engagement. Exporta tus leads con un clic, listos para usar donde los necesites.', metricLabel:'tasa de satisfacción' },
      ],
    },
    problem: {
      title:'Hoy, capturar contactos es un proceso roto.',
      sub:'Las empresas invierten en generar contactos, pero no tienen un sistema que convierta esos momentos en resultados medibles.',
      source:'Estadísticas basadas en estudios públicos de la industria.',
      items:[
        { stat:'88%', desc:'de las tarjetas de papel se descartan en la primera semana tras un evento de networking.' },
        { stat:'60–70%', desc:'de los contactos que haces nunca llegan a un CRM: la información se pierde entre el papel y el registro manual.' },
        { stat:'73%', desc:'de los leads nunca reciben ningún seguimiento: ni llamada, ni correo, ni mensaje.' },
        { stat:'44%', desc:'de los vendedores se rinde después de un solo intento de seguimiento, aunque en promedio se necesitan cinco.' },
      ],
    },
    leadcapture: {
      title:'Es momento de transformar tus eventos en un motor de crecimiento escalable.',
      tabs:[
        { label:'Captura de Leads', items:[
          { title:'Escaneo universal', desc:'Un solo escáner con IA que funciona en cualquier evento, feria o conferencia.' },
          { title:'Calificación al instante', desc:'Tu equipo captura el contexto clave en segundos y cada lead queda bien clasificado.' },
          { title:'Sincroniza con tu CRM', desc:'Envía tus leads y contactos a tu CRM en segundos. Sin planillas ni carga manual.' },
        ]},
        { label:'Tarjetas Digitales', items:[
          { title:'Marca unificada', desc:'Estandariza la imagen de tu empresa en tarjetas digitales.' },
          { title:'Mejores primeras impresiones', desc:'Sorprende a tus clientes y contactos con tarjetas digitales que reflejan tu marca.' },
          { title:'Networking simplificado', desc:'Convierte cada saludo en una oportunidad. Captura cada contacto y sincronízalo al instante.' },
        ]},
      ],
    },
    howit: [
      { title:'Activa tu perfil', desc:'Activa tu perfil sin configuración técnica.' },
      { title:'Personaliza tu información', desc:'Agrega tus redes, tu información de contacto y tu marca personal en minutos.' },
      { title:'Comparte con un toque', desc:'Acerca tu perfil a cualquier celular y tu información queda guardada al instante.' },
    ],
    security: {
      title1:'Tu información,', title2:'siempre protegida.',
      sub:'Seguridad pensada para equipos que gestionan datos de contacto todos los días.',
      items:[
        { title:'Cifrado de extremo a extremo', desc:'Tus datos viajan y se almacenan cifrados en todo momento.' },
        { title:'Control de acceso por roles', desc:'Define permisos por miembro de tu equipo y protege la información sensible.' },
        { title:'Copias de seguridad automáticas', desc:'Respaldamos tu información de forma continua para que nunca se pierda.' },
        { title:'Privacidad por diseño', desc:'Seguimos buenas prácticas de protección de datos para ti y tus contactos.' },
      ],
    },
    ghc: {
      title1:'Todo lo que tu equipo necesita', titlePre:'para', titleWord:'conectar', titlePost:'mejor.',
      sub:'Explora las herramientas de CONTACT: perfiles, leads, equipos y analíticas desde una sola plataforma.',
      caption:'La plataforma completa para conectar y gestionar tu equipo.',
      items:[
        {title:'Mis Perfiles', desc:'Administra tus perfiles digitales, tarjetas y enlaces desde una interfaz simple.'},
        {title:'Analytics', desc:'Visualiza visitas, clics, contactos guardados y tasa de interacción en tiempo real.'},
        {title:'Teams', desc:'Gestiona perfiles, usuarios y equipos comerciales desde un panel corporativo.'},
        {title:'Leads', desc:'Identifica y gestiona los contactos que interactúan con tu perfil.'},
        {title:'Hub', desc:'Centraliza tus conexiones, acciones y oportunidades desde un mismo lugar.'},
        {title:'Apariencia', desc:'Personaliza tu perfil digital para mantener una presencia profesional y alineada a tu marca.'},
      ],
    },
    bento: { analyticsTitle:'Cada visita, cada clic, medido al instante.', walletTitle:'Guarda tu perfil en tu Wallet, en un toque.', appleWallet:'Apple Wallet', googleWallet:'Google Wallet' },
    fan: { title:'Un toque. Toda tu información.', sub:'Sin apps, sin fricciones. Comparte tu perfil profesional con un solo toque.' },
    globeCta: { dark:'Construido para conectar profesionales.', light:'Comparte tu perfil, mide cada interacción y haz crecer tu red, sin importar dónde estés.', cta:'Únete hoy' },
    api: {
      eyebrow:'API',
      title1:'Tus leads, sincronizados con', title2:'las herramientas que ya usas.',
      sub:'Vía nuestra API de solo lectura conectas tus leads a tu CRM, hojas de cálculo o automatizaciones. No es una integración nativa, pero tienes control total de qué sincronizas y cuándo.',
      cta:'Ver documentación',
    },
    integrations: {
      eyebrow:'INTEGRACIONES',
      title1:'Construido para mantener todo tu ecosistema', title2:'conectado.',
      sub:'Integra las herramientas que tu equipo ya utiliza, sincroniza la información automáticamente y convierte procesos dispersos en un flujo de trabajo continuo.',
    },
    social: { more:'y más!' },
    plans: {
      title:'Planes para cada escala.', sub:'Empieza gratis. Actualiza cuando lo necesites.',
      monthly:'Mensual', yearly:'Anual', save:'2 meses gratis', recommended:'Recomendado', contactSales:'Contacta a ventas',
      list:[
        { name:'Gratuito', desc:'Gratis para siempre. Comparte tu contacto como un profesional, sin límite de tiempo.', features:['Conexiones ilimitadas','1 perfil digital','Analíticas básicas','Leads limitados /mes','Apple Wallet & Google Wallet','QR code y link único'], cta:'Crear cuenta' },
        { name:'Pro', desc:'Para profesionales que quieren saber qué pasa con cada contacto y actuar antes de que se enfríe.', features:['Todo lo del Gratuito','Analytics avanzadas','Leads ilimitados','Identificación de leads','Seguimiento inteligente','Personalización avanzada','Integraciones con tus herramientas','Acceso API y Webhooks'], priceNote:'precio de lanzamiento', cta:'Empezar con Pro' },
        { name:'Enterprise', priceTagline:'Precio a medida según el tamaño de tu equipo.', desc:'Para negocios con varias personas en terreno: un panel central para ver quién está capturando y convirtiendo en cada evento.', features:['Ranking de equipo por campaña','Múltiples perfiles administrados','Analítica de equipo','Gestión de equipos (Teams)','Soporte prioritario','Todo lo del Pro'], cta:'Agendar reunión' },
      ],
    },
    faq: {
      title:'Preguntas frecuentes', sub:'Todo lo que necesitas saber antes de empezar.',
      items:[
        {q:'¿Necesito una app para usar CONTACT?', a:'Para compartir tu contacto no necesitas una app, pero para gestionar tu información y tu red, descarga la app de Contact o hazlo desde la web.'},
        {q:'¿Puedo actualizar mi información después de comprar?', a:'Sí, en cualquier momento. Los cambios se reflejan en tiempo real: todos los que ya tienen tu contacto verán la versión actualizada sin que tengas que hacer nada extra.'},
        {q:'¿Con qué teléfonos es compatible la tarjeta NFC?', a:'Con todos los smartphones modernos. En iPhone funciona desde el iPhone 7 (iOS 14+). En Android funciona con cualquier equipo que tenga NFC activado, que incluye prácticamente todos los modelos desde 2015.'},
        {q:'¿Qué pasa si pierdo mi tarjeta?', a:'Tu información y perfil están en la plataforma, no en la tarjeta. Puedes desactivar la tarjeta perdida y pedir una de reemplazo. Tus datos y estadísticas se mantienen intactos.'},
        {q:'¿Las analíticas son en tiempo real?', a:'Sí. Puedes ver visitas, clics por red social, contactos guardados y tendencias por período desde tu dashboard, actualizado en tiempo real.'},
        {q:'¿Puedo tener más de un perfil?', a:'Sí. El plan Gratuito incluye 1 perfil. Con el plan Pro y los planes de equipo puedes tener múltiples perfiles para distintos roles o proyectos.'},
      ],
    },
    footer: {
      product:'Producto', features:'Características', plans:'Planes', schedule:'Agendar reunión', docs:'Documentación',
      company:'Empresa', about:'Nosotros',
      legal:'Legal', terms:'Términos y Condiciones', privacy:'Política de Privacidad', dataProcessing:'Tratamiento de Datos', hours:'Lun–Vie 8:00–20:00 hrs',
      security:'Seguridad', securityText:'Tus datos viajan cifrados con TLS y nunca los compartimos con terceros. Tú decides qué información mostrar y cuándo actualizarla.',
      copyright:'© 2026 CONTACT Network SPA · Chile', tagline:'Conecta. Comparte. Crece.',
    },
    quote: {
      title:'Solicitar cotización', sub:'Te responderemos en menos de 24 horas hábiles.',
      successTitle:'Solicitud recibida', successBody:'Te contactaremos dentro de las próximas 24 horas hábiles con una propuesta personalizada.',
      company:'Empresa', contactPerson:'Persona de contacto', email:'Correo electrónico', phone:'Teléfono',
      estimated:'Cantidad estimada', selectRange:'Selecciona un rango', ranges:'Rangos', exact:'Exacto', min:'Mínimo 1',
      description:'Descripción del proyecto', descriptionPh:'Cuéntanos sobre tu empresa y necesidades...',
      sending:'Enviando...', submit:'Solicitar cotización',
      companyPh:'ACME S.A.', contactPersonPh:'Ana Rodríguez', emailPh:'ana@empresa.com', phonePh:'+56 9 1234 5678',
    },
  },
  en: {
    nav: { features:'Features', plans:'Pricing', solution:'Solution', login:'Sign in', buy:'Buy', access:'Sign in', quote:'Get a quote', myProfile:'My profile', dashboard:'Dashboard', settings:'Settings', logout:'Log out' },
    hero: { trust:'150+ active companies · 2,500+ professionals', connectPre:'Share your', touch:'profile', measurePre:'Measure every', opportunity:'opportunity', sub:'Turn your professional connections into concrete results.', cta:'Get started', trustLabel:'Trusted by' },
    feat: {
      title1:'Turn every contact', title2:'into an opportunity.',
      sub:'Tools designed for professionals and teams that measure every contact.',
      cta:'Request a quote',
      cards:[
        { headline:'Your professional identity, available in one tap.', body:'A single tap shares all your information. No extra apps. Update anytime and your contacts will always see the current version.', metricLabel:'more connections logged' },
        { headline:'Data-driven decisions, in real time.', body:'Executive dashboard with visit, conversion and engagement metrics. Export your leads with one click, ready to use wherever you need them.', metricLabel:'satisfaction rate' },
      ],
    },
    problem: {
      title:'Today, capturing contacts is a broken process.',
      sub:'Companies invest in generating contacts, but lack a system that turns those moments into measurable results.',
      source:'Statistics based on public industry research.',
      items:[
        { stat:'88%', desc:'of paper business cards are discarded within the first week after a networking event.' },
        { stat:'60–70%', desc:'of the contacts you make never reach a CRM: the information gets lost between paper and manual entry.' },
        { stat:'73%', desc:'of leads never receive any follow-up at all: no call, no email, no message.' },
        { stat:'44%', desc:'of salespeople give up after a single follow-up attempt, even though five are needed on average.' },
      ],
    },
    leadcapture: {
      title:'It’s time to transform your events into a scalable growth engine.',
      tabs:[
        { label:'Lead Capture', items:[
          { title:'Universal scanning', desc:'One AI-powered scanner that works at every event, conference and trade show.' },
          { title:'Instant qualification', desc:'Your reps capture key context in seconds, and every lead is properly classified.' },
          { title:'Sync with your CRM', desc:'Send your leads and contacts to your CRM in seconds. No spreadsheets, no manual entry.' },
        ]},
        { label:'Digital Business Cards', items:[
          { title:'Unified branding', desc:'Standardize your company brand across digital cards.' },
          { title:'Better first impressions', desc:'Impress your clients and contacts with digital cards that reflect your brand.' },
          { title:'Streamlined networking', desc:'Turn every handshake into an opportunity. Capture every contact and sync it instantly.' },
        ]},
      ],
    },
    howit: [
      { title:'Activate your profile', desc:'Activate your profile with no technical setup.' },
      { title:'Customize your information', desc:'Add your social links, contact information and personal brand in minutes.' },
      { title:'Share with a tap', desc:'Bring your profile close to any phone and your information is saved instantly.' },
    ],
    security: {
      title1:'Your information,', title2:'always protected.',
      sub:'Security built for teams that handle contact data every day.',
      items:[
        { title:'End-to-end encryption', desc:'Your data travels and is stored encrypted at all times.' },
        { title:'Role-based access control', desc:'Set permissions per team member and protect sensitive information.' },
        { title:'Automatic backups', desc:'We continuously back up your information so it never gets lost.' },
        { title:'Privacy by design', desc:'We follow data protection best practices for you and your contacts.' },
      ],
    },
    ghc: {
      title1:'Everything your team needs', titlePre:'to', titleWord:'connect', titlePost:'better.',
      sub:'Explore CONTACT’s tools: profiles, leads, teams and analytics from a single platform.',
      caption:'The complete platform to connect and manage your team.',
      items:[
        {title:'My Profiles', desc:'Manage your digital profiles, cards and links from a simple interface.'},
        {title:'Analytics', desc:'View visits, clicks, saved contacts and engagement rate in real time.'},
        {title:'Teams', desc:'Manage profiles, users and sales teams from a corporate panel.'},
        {title:'Leads', desc:'Identify and manage the contacts that interact with your profile.'},
        {title:'Hub', desc:'Centralize your connections, actions and opportunities in one place.'},
        {title:'Appearance', desc:'Customize your digital profile to keep a professional presence aligned with your brand.'},
      ],
    },
    bento: { analyticsTitle:'Every visit, every click, measured instantly.', walletTitle:'Save your profile to your Wallet, in one tap.', appleWallet:'Apple Wallet', googleWallet:'Google Wallet' },
    fan: { title:'One tap. All your information.', sub:'No apps, no friction. Share your professional profile with a single tap.' },
    globeCta: { dark:'Built to connect professionals.', light:'Share your profile, measure every interaction and grow your network, wherever you are.', cta:'Join today' },
    api: {
      eyebrow:'API',
      title1:'Your leads, synced with', title2:'the tools you already use.',
      sub:'Via our read-only API, connect your leads to your CRM, spreadsheets or automations. It\'s not a native integration, but you get full control over what syncs and when.',
      cta:'View documentation',
    },
    integrations: {
      eyebrow:'INTEGRATIONS',
      title1:'Automate your workflow,', title2:'without writing a single line of code.',
      sub:'Connect CONTACT with your CRM, marketing platforms or automation tools in minutes. Pick a ready-made integration or build your own with webhooks.',
    },
    social: { more:'and more!' },
    plans: {
      title:'Plans for every scale.', sub:'Start for free. Upgrade whenever you need.',
      monthly:'Monthly', yearly:'Yearly', save:'2 months free', recommended:'Recommended', contactSales:'Contact sales',
      list:[
        { name:'Free', desc:'The essentials to connect and share.', features:['Unlimited connections','1 digital profile','Basic analytics','Limited leads /mo','Apple Wallet & Google Wallet','QR code and unique link'], cta:'Create account' },
        { name:'Pro', desc:'For professionals who measure and convert every contact.', features:['Everything in Free','Advanced analytics','Unlimited leads','Lead identification','Smart follow-up','Advanced customization','Integrations with your tools','API & Webhooks access'], priceNote:'launch price', cta:'Start with Pro' },
        { name:'Enterprise', priceTagline:'Custom pricing based on your team size.', desc:'For companies managing teams that need full control.', features:['Everything in Pro','Team management (Teams)','Team analytics','Multiple managed profiles','Priority support'], cta:'Book a meeting' },
      ],
    },
    faq: {
      title:'Frequently asked questions', sub:'Everything you need to know before getting started.',
      items:[
        {q:'Do I need an app to use CONTACT?', a:'To share your contact you don\'t need an app, but to manage your information and network, download the Contact app or do it from the web.'},
        {q:'Can I update my information after buying?', a:'Yes, at any time. Changes reflect in real time: everyone who already has your contact will see the updated version without any extra steps on your part.'},
        {q:'Which phones is the NFC card compatible with?', a:'With all modern smartphones. On iPhone it works from iPhone 7 onward (iOS 14+). On Android it works with any device with NFC enabled, which covers practically every model since 2015.'},
        {q:'What happens if I lose my card?', a:'Your information and profile live on the platform, not on the card. You can deactivate the lost card and request a replacement. Your data and stats stay intact.'},
        {q:'Is the analytics data real-time?', a:'Yes. You can see visits, clicks by social network, saved contacts and trends by period from your dashboard, updated in real time.'},
        {q:'Can I have more than one profile?', a:'Yes. The Free plan includes 1 profile. With the Pro plan and team plans you can have multiple profiles for different roles or projects.'},
      ],
    },
    footer: {
      product:'Product', features:'Features', plans:'Pricing', schedule:'Book a meeting', docs:'Documentation',
      company:'Company', about:'About us',
      legal:'Legal', terms:'Terms and Conditions', privacy:'Privacy Policy', dataProcessing:'Data Processing', hours:'Mon–Fri 8:00 AM–8:00 PM',
      security:'Security', securityText:'Your data travels encrypted with TLS and we never share it with third parties. You decide what information to show and when to update it.',
      copyright:'© 2026 CONTACT Network SPA · Chile', tagline:'Connect. Share. Grow.',
    },
    quote: {
      title:'Request a quote', sub:'We’ll get back to you in less than 24 business hours.',
      successTitle:'Request received', successBody:'We’ll contact you within the next 24 business hours with a custom proposal.',
      company:'Company', contactPerson:'Contact person', email:'Email address', phone:'Phone number',
      estimated:'Estimated quantity', selectRange:'Select a range', ranges:'Ranges', exact:'Exact', min:'Minimum 1',
      description:'Project description', descriptionPh:'Tell us about your company and needs...',
      sending:'Sending...', submit:'Request a quote',
      companyPh:'ACME Inc.', contactPersonPh:'Jane Smith', emailPh:'jane@company.com', phonePh:'+1 415 555 0100',
    },
  },
  pt: {
    nav: { features:'Funcionalidades', plans:'Planos', solution:'Solução', login:'Entrar', buy:'Comprar', access:'Acessar', quote:'Orçamento', myProfile:'Meu perfil', dashboard:'Dashboard', settings:'Configurações', logout:'Sair' },
    hero: { trust:'150+ empresas ativas · 2.500+ profissionais', connectPre:'Compartilhe seu', touch:'perfil', measurePre:'Meça cada', opportunity:'oportunidade', sub:'Transforme suas conexões profissionais em resultados concretos.', cta:'Começar', trustLabel:'Confiam em nós' },
    feat: {
      title1:'Transforme cada contato', title2:'em uma oportunidade.',
      sub:'Ferramentas criadas para profissionais e equipes que medem cada contato.',
      cta:'Solicitar orçamento',
      cards:[
        { headline:'Sua identidade profissional, disponível em um toque.', body:'Um único toque compartilha todas as suas informações. Sem aplicativos adicionais. Atualize a qualquer momento e seus contatos sempre verão a versão mais recente.', metricLabel:'mais conexões registradas' },
        { headline:'Decisões baseadas em dados, em tempo real.', body:'Painel executivo com métricas de visitas, conversão e engajamento. Exporte seus leads com um clique, prontos para usar onde precisar.', metricLabel:'taxa de satisfação' },
      ],
    },
    problem: {
      title:'Hoje, capturar contatos é um processo quebrado.',
      sub:'As empresas investem em gerar contatos, mas não têm um sistema que transforme esses momentos em resultados mensuráveis.',
      source:'Estatísticas baseadas em estudos públicos do setor.',
      items:[
        { stat:'88%', desc:'dos cartões de papel são descartados na primeira semana após um evento de networking.' },
        { stat:'60–70%', desc:'dos contatos que você faz nunca chegam a um CRM: a informação se perde entre o papel e o registro manual.' },
        { stat:'73%', desc:'dos leads nunca recebem nenhum acompanhamento: nem ligação, nem e-mail, nem mensagem.' },
        { stat:'44%', desc:'dos vendedores desistem após uma única tentativa de acompanhamento, embora em média sejam necessárias cinco.' },
      ],
    },
    leadcapture: {
      title:'É hora de transformar seus eventos em um motor de crescimento escalável.',
      tabs:[
        { label:'Captura de Leads', items:[
          { title:'Escaneamento universal', desc:'Um único scanner com IA que funciona em qualquer evento, feira ou conferência.' },
          { title:'Qualificação instantânea', desc:'Sua equipe captura o contexto chave em segundos e cada lead fica bem classificado.' },
          { title:'Sincronize com seu CRM', desc:'Envie seus leads e contatos para o seu CRM em segundos. Sem planilhas nem cadastro manual.' },
        ]},
        { label:'Cartões Digitais', items:[
          { title:'Marca unificada', desc:'Padronize a imagem da sua empresa em cartões digitais.' },
          { title:'Melhores primeiras impressões', desc:'Surpreenda seus clientes e contatos com cartões digitais que refletem sua marca.' },
          { title:'Networking simplificado', desc:'Transforme cada cumprimento em uma oportunidade. Capture cada contato e sincronize instantaneamente.' },
        ]},
      ],
    },
    howit: [
      { title:'Ative seu perfil', desc:'Ative seu perfil sem configuração técnica.' },
      { title:'Personalize suas informações', desc:'Adicione suas redes, informações de contato e marca pessoal em minutos.' },
      { title:'Compartilhe com um toque', desc:'Aproxime seu perfil de qualquer celular e suas informações ficam salvas instantaneamente.' },
    ],
    security: {
      title1:'Suas informações,', title2:'sempre protegidas.',
      sub:'Segurança pensada para equipes que gerenciam dados de contato todos os dias.',
      items:[
        { title:'Criptografia de ponta a ponta', desc:'Seus dados trafegam e são armazenados criptografados o tempo todo.' },
        { title:'Controle de acesso por funções', desc:'Defina permissões para cada membro da sua equipe e proteja informações sensíveis.' },
        { title:'Backups automáticos', desc:'Fazemos backup das suas informações de forma contínua para que nunca se percam.' },
        { title:'Privacidade por design', desc:'Seguimos boas práticas de proteção de dados para você e seus contatos.' },
      ],
    },
    ghc: {
      title1:'Tudo o que sua equipe precisa', titlePre:'para', titleWord:'conectar', titlePost:'melhor.',
      sub:'Explore as ferramentas da CONTACT: perfis, leads, equipes e análises em uma única plataforma.',
      caption:'A plataforma completa para conectar e gerenciar sua equipe.',
      items:[
        {title:'Meus Perfis', desc:'Gerencie seus perfis digitais, cartões e links em uma interface simples.'},
        {title:'Analytics', desc:'Visualize visitas, cliques, contatos salvos e taxa de interação em tempo real.'},
        {title:'Teams', desc:'Gerencie perfis, usuários e equipes comerciais em um painel corporativo.'},
        {title:'Leads', desc:'Identifique e gerencie os contatos que interagem com seu perfil.'},
        {title:'Hub', desc:'Centralize suas conexões, ações e oportunidades em um só lugar.'},
        {title:'Aparência', desc:'Personalize seu perfil digital para manter uma presença profissional alinhada à sua marca.'},
      ],
    },
    bento: { analyticsTitle:'Cada visita, cada clique, medido instantaneamente.', walletTitle:'Salve seu perfil na sua Wallet, em um toque.', appleWallet:'Apple Wallet', googleWallet:'Google Wallet' },
    fan: { title:'Um toque. Todas as suas informações.', sub:'Sem aplicativos, sem atritos. Compartilhe seu perfil profissional com um único toque.' },
    globeCta: { dark:'Construído para conectar profissionais.', light:'Compartilhe seu perfil, meça cada interação e faça sua rede crescer, não importa onde você esteja.', cta:'Junte-se hoje' },
    api: {
      eyebrow:'API',
      title1:'Seus leads, sincronizados com', title2:'as ferramentas que você já usa.',
      sub:'Via nossa API somente leitura você conecta seus leads ao seu CRM, planilhas ou automações. Não é uma integração nativa, mas você tem controle total sobre o que sincroniza e quando.',
      cta:'Ver documentação',
    },
    integrations: {
      eyebrow:'INTEGRAÇÕES',
      title1:'Construído para manter todo o seu ecossistema', title2:'conectado.',
      sub:'Integre as ferramentas que sua equipe já usa, sincronize as informações automaticamente e transforme processos dispersos em um fluxo de trabalho contínuo.',
    },
    social: { more:'e muito mais!' },
    plans: {
      title:'Planos para cada escala.', sub:'Comece grátis. Atualize quando precisar.',
      monthly:'Mensal', yearly:'Anual', save:'2 meses grátis', recommended:'Recomendado', contactSales:'Fale com vendas',
      list:[
        { name:'Gratuito', desc:'O essencial para conectar e compartilhar.', features:['Conexões ilimitadas','1 perfil digital','Analytics básico','Leads limitados /mês','Apple Wallet e Google Wallet','QR code e link único'], cta:'Criar conta' },
        { name:'Pro', desc:'Para profissionais que medem e convertem cada contato.', features:['Tudo do Gratuito','Analytics avançado','Leads ilimitados','Identificação de leads','Acompanhamento inteligente','Personalização avançada','Integrações com suas ferramentas','Acesso a API e Webhooks'], priceNote:'preço de lançamento', cta:'Começar com Pro' },
        { name:'Enterprise', priceTagline:'Preço sob medida de acordo com o tamanho da sua equipe.', desc:'Para empresas que gerenciam equipes e precisam de controle total.', features:['Tudo do Pro','Gestão de equipes (Teams)','Análise de equipe','Múltiplos perfis administrados','Suporte prioritário'], cta:'Agendar reunião' },
      ],
    },
    faq: {
      title:'Perguntas frequentes', sub:'Tudo o que você precisa saber antes de começar.',
      items:[
        {q:'Preciso de um aplicativo para usar a CONTACT?', a:'Para compartilhar seu contato você não precisa de um app, mas para gerenciar suas informações e sua rede, baixe o app da Contact ou faça isso pela web.'},
        {q:'Posso atualizar minhas informações depois de comprar?', a:'Sim, a qualquer momento. As alterações são refletidas em tempo real: todos que já têm seu contato verão a versão atualizada sem que você precise fazer nada a mais.'},
        {q:'Com quais celulares o cartão NFC é compatível?', a:'Com todos os smartphones modernos. No iPhone, funciona a partir do iPhone 7 (iOS 14+). No Android, funciona com qualquer aparelho que tenha NFC ativado, o que inclui praticamente todos os modelos desde 2015.'},
        {q:'O que acontece se eu perder meu cartão?', a:'Suas informações e seu perfil estão na plataforma, não no cartão. Você pode desativar o cartão perdido e solicitar um substituto. Seus dados e estatísticas permanecem intactos.'},
        {q:'As análises são em tempo real?', a:'Sim. Você pode ver visitas, cliques por rede social, contatos salvos e tendências por período no seu painel, atualizado em tempo real.'},
        {q:'Posso ter mais de um perfil?', a:'Sim. O plano Gratuito inclui 1 perfil. Com o plano Pro e os planos de equipe, você pode ter múltiplos perfis para diferentes funções ou projetos.'},
      ],
    },
    footer: {
      product:'Produto', features:'Funcionalidades', plans:'Planos', schedule:'Agendar reunião', docs:'Documentação',
      company:'Empresa', about:'Sobre nós',
      legal:'Legal', terms:'Termos e Condições', privacy:'Política de Privacidade', dataProcessing:'Tratamento de Dados', hours:'Seg–Sex 8h–20h',
      security:'Segurança', securityText:'Seus dados trafegam criptografados com TLS e nunca são compartilhados com terceiros. Você decide quais informações mostrar e quando atualizá-las.',
      copyright:'© 2026 CONTACT Network SPA · Chile', tagline:'Conecte. Compartilhe. Cresça.',
    },
    quote: {
      title:'Solicitar orçamento', sub:'Responderemos em menos de 24 horas úteis.',
      successTitle:'Solicitação recebida', successBody:'Entraremos em contato dentro das próximas 24 horas úteis com uma proposta personalizada.',
      company:'Empresa', contactPerson:'Pessoa de contato', email:'E-mail', phone:'Telefone',
      estimated:'Quantidade estimada', selectRange:'Selecione uma faixa', ranges:'Faixas', exact:'Exato', min:'Mínimo 1',
      description:'Descrição do projeto', descriptionPh:'Conte-nos sobre sua empresa e necessidades...',
      sending:'Enviando...', submit:'Solicitar orçamento',
      companyPh:'ACME S.A.', contactPersonPh:'Ana Rodrigues', emailPh:'ana@empresa.com', phonePh:'+56 9 1234 5678',
    },
  },
};

/* ─── GLOBE (canvas puro, sin librerías externas) ─────────── */
const GLOBE_MARKERS = [
  { lat: 14.5995, lon: 120.9842 },
  { lat: 19.076, lon: 72.8777 },
  { lat: 23.8103, lon: 90.4125 },
  { lat: 30.0444, lon: 31.2357 },
  { lat: 39.9042, lon: 116.4074 },
  { lat: -23.5505, lon: -46.6333 },
  { lat: 19.4326, lon: -99.1332 },
  { lat: 40.7128, lon: -74.006 },
  { lat: -33.4489, lon: -70.6693 }, // Santiago
];
const GlobeCanvas = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let size = 0;

    const N = 900;
    const points = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const radius = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = goldenAngle * i;
      points.push([Math.cos(theta) * radius, y, Math.sin(theta) * radius]);
    }
    const markerPoints = GLOBE_MARKERS.map(({ lat, lon }) => {
      const phiM = (90 - lat) * Math.PI / 180;
      const thetaM = (lon + 180) * Math.PI / 180;
      return [
        -Math.sin(phiM) * Math.cos(thetaM),
        Math.cos(phiM),
        Math.sin(phiM) * Math.sin(thetaM),
      ];
    });

    const setSize = () => {
      size = canvas.offsetWidth;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    // Pre-bake the marker glow once (same ctx.shadowBlur look as before) into
    // a small offscreen sprite, instead of paying the blur cost on every
    // frame for every marker — drawImage is far cheaper than shadowBlur.
    const markerSize = 32;
    const markerSprite = document.createElement('canvas');
    markerSprite.width = markerSize * dpr;
    markerSprite.height = markerSize * dpr;
    const spriteCtx = markerSprite.getContext('2d');
    spriteCtx.scale(dpr, dpr);
    spriteCtx.beginPath();
    spriteCtx.arc(markerSize / 2, markerSize / 2, 3.5, 0, Math.PI * 2);
    spriteCtx.fillStyle = '#0071e3';
    spriteCtx.shadowColor = 'rgba(0,113,227,.55)';
    spriteCtx.shadowBlur = 9;
    spriteCtx.fill();

    let angle = 0;
    let raf;
    let active = true;
    const render = () => {
      if (active) {
        angle += 0.0022;
        ctx.clearRect(0, 0, size, size);
        const R = size * 0.46;
        const cx = size / 2, cy = size / 2;
        const cosA = Math.cos(angle), sinA = Math.sin(angle);

        points.forEach(([x, y, z]) => {
          const rx = x * cosA - z * sinA;
          const rz = x * sinA + z * cosA;
          if (rz < -0.08) return;
          const px = cx + rx * R;
          const py = cy - y * R;
          const depth = (rz + 1) / 2;
          ctx.beginPath();
          ctx.arc(px, py, 0.9 + depth * 0.8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(29,29,31,${0.12 + depth * 0.42})`;
          ctx.fill();
        });

        markerPoints.forEach(([x, y, z]) => {
          const rx = x * cosA - z * sinA;
          const rz = x * sinA + z * cosA;
          if (rz < -0.05) return;
          const px = cx + rx * R;
          const py = cy - y * R;
          ctx.drawImage(markerSprite, px - markerSize / 2, py - markerSize / 2, markerSize, markerSize);
        });
      }

      raf = requestAnimationFrame(render);
    };
    render();

    const obs = new ResizeObserver(setSize);
    obs.observe(canvas);
    // Unlike HeroDither/GHCMeshBg (which hand a speed prop to a shader
    // library), this loop draws by hand — pausing it once scrolled out of
    // view means gating the draw calls directly, same idea as
    // HorizonGlowBg's `active` flag above in this file.
    const visObs = new IntersectionObserver(([e]) => { active = e.isIntersecting; }, { rootMargin: '150px' });
    visObs.observe(canvas);
    return () => { cancelAnimationFrame(raf); obs.disconnect(); visObs.disconnect(); };
  }, []);
  return <canvas ref={canvasRef} className="nx-globe-canvas" />;
};

/* ─── HOMEPAGE ───────────────────────────────────────────── */
const Homepage = React.memo(() => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [accountMenuOpen]);

  const userInitial = (user?.name || '').trim().charAt(0).toUpperCase() || '?';
  const userFirstName = (user?.name || '').trim().split(' ')[0];
  const [avatarPhotoUrl, setAvatarPhotoUrl] = useState(null);

  // The account menu's avatar should show the real photo from the user's
  // default digital profile, not just an initial — this page only has the
  // auth user (name/email), so the photo has to be fetched separately.
  // Non-critical: any failure (no profiles yet, network hiccup) just keeps
  // the initial-letter fallback instead of surfacing an error.
  useEffect(() => {
    if (!user) { setAvatarPhotoUrl(null); return; }
    let ignore = false;
    axios.get(`${API}/profiles/me`)
      .then((res) => {
        if (ignore) return;
        const profiles = Array.isArray(res.data) ? res.data : [];
        const defaultProfile = profiles.find((p) => p.is_default) || profiles[0];
        setAvatarPhotoUrl(defaultProfile?.profile_image_url || null);
      })
      .catch(() => { if (!ignore) setAvatarPhotoUrl(null); });
    return () => { ignore = true; };
  }, [user?.id]);

  const AccountAvatar = ({ className }) => (
    avatarPhotoUrl
      ? <img src={avatarPhotoUrl} alt={userFirstName} className={`${className} nx-account-avatar--img`} />
      : <span className={className}>{userInitial}</span>
  );

  const goToAccountTab = (tab) => {
    setAccountMenuOpen(false);
    setMobileNavOpen(false);
    navigate(tab ? `/dashboard?tab=${tab}` : '/dashboard');
  };

  const handleLogout = () => {
    setAccountMenuOpen(false);
    setMobileNavOpen(false);
    logout();
    navigate('/');
  };
  const [quotationData, setQuotationData]   = useState({ company:'', contact_person:'', email:'', phone:'', estimated_cards:'', comments:'' });
  const [quotationSent, setQuotationSent]   = useState(false);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [priceRanges, setPriceRanges]       = useState([]);
  const [quantityType, setQuantityType]     = useState('preset');
  const [customQuantity, setCustomQuantity] = useState(1);
  const [quantityError, setQuantityError]   = useState('');
  const [showTermsModal, setShowTermsModal]   = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDataProcessingModal, setShowDataProcessingModal] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [navBorder, setNavBorder]           = useState(false);
  const [billing, setBilling]               = useState('monthly');
  const billingYearlyRef = useRef(null);
  // Display-only currency reference for the pricing section — billing
  // always happens in CLP via MercadoPago, this just converts the shown
  // number for visitors browsing in another currency. Defaults to CLP and
  // remembers the visitor's choice across visits.
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem('contact_currency') || 'CLP'; } catch { return 'CLP'; }
  });
  const [exchangeRates, setExchangeRates] = useState(null);
  // Pro's price is admin-editable (see AdminPlansTab / /admin/plans/pricing);
  // the '$2.500'/'$25.000' literals below are only the fallback shown until
  // this resolves, so the section never renders blank on first paint.
  const [planPricing, setPlanPricing] = useState(null);
  const SUPPORTED_CURRENCIES = ['CLP', 'USD', 'EUR', 'MXN', 'ARS', 'BRL'];
  const handleCurrencyChange = (next) => {
    setCurrency(next);
    try { localStorage.setItem('contact_currency', next); } catch {}
  };
  const formatPrice = (clpString) => {
    if (currency === 'CLP' || !exchangeRates || !exchangeRates[currency] || !clpString) return clpString;
    const digits = clpString.replace(/[^0-9]/g, '');
    if (!digits) return clpString;
    const converted = parseInt(digits, 10) * exchangeRates[currency];
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(converted);
    } catch { return clpString; }
  };
  const [mobilePlanTab, setMobilePlanTab] = useState(1);
  const handleSetBilling = (val) => {
    if (val === 'yearly' && billing !== 'yearly' && billingYearlyRef.current) {
      const rect = billingYearlyRef.current.getBoundingClientRect();
      // Loaded on demand — this fires once per session at most (switching to
      // yearly billing), so there's no reason to ship it in the initial bundle.
      import('canvas-confetti').then(({ default: confetti }) => {
        confetti({
          particleCount: 60,
          spread: 65,
          origin: { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight },
          colors: ['#0071e3', '#34C759', '#AF52DE', '#FF9500'],
          ticks: 200,
          gravity: 1.1,
          decay: 0.94,
          startVelocity: 28,
          scalar: 0.9,
        });
      });
    }
    setBilling(val);
  };
  const [mobileNavOpen, setMobileNavOpen]   = useState(false);
  const [heroReady, setHeroReady]           = useState(true);
  const [lang, setLang]                     = useState('es');
  const t = TR[lang];

  useEffect(() => {
    if (heroReady) {
      window.__nxHeroReady = true;
      window.dispatchEvent(new Event('nx:hero-ready'));
    } else {
      window.__nxHeroReady = false;
    }
  }, [heroReady]);

  useEffect(() => {
    axios.get(`${API}/public/quotations/config`)
      .then((res) => { if (res.data?.price_table) setPriceRanges(res.data.price_table); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/exchange-rates`)
      .then((res) => { if (res.data?.rates) setExchangeRates(res.data.rates); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/plans/catalog`)
      .then((res) => {
        const pro = res.data?.plans?.find((p) => p.id === 'pro');
        if (pro?.price_clp != null && pro?.price_clp_annual != null) {
          setPlanPricing({ price_clp: pro.price_clp, price_clp_annual: pro.price_clp_annual });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fn = () => setNavBorder(window.scrollY > 24);    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (quantityType === 'custom' && (!parseInt(customQuantity) || parseInt(customQuantity) < 1)) {
      setQuantityError(lang === 'en' ? 'Enter a valid quantity.' : lang === 'pt' ? 'Insira uma quantidade válida.' : 'Ingresa una cantidad válida.'); return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotations`, quotationData);
      setQuotationSent(true);
      setQuotationData({ company:'', contact_person:'', email:'', phone:'', estimated_cards:'', comments:'' });
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  const openModal  = () => setShowQuotationModal(true);
  const closeModal = () => { setShowQuotationModal(false); setQuotationSent(false); };
  // Logos "Confían en nosotros" hardcodeados (antes venían del CMS vía fetch
  // async, lo que generaba un salto visible al cargar). Al ir directo en el
  // código cargan igual de inmediato que el resto de las imágenes de la
  // página. Mismo orden que el diseño aprobado (2 filas de 4).
  const logos = [
    { name:'BICE',       image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782524053/contact/landing/tywy08ejjuslqb7nlqan.png' },
    { name:'S4E',        image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534411/contact/landing/xbp3rfyo09fcgqnguctt.png' },
    { name:'DXC',        image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788921433/DXC-Veritcal-Tagline-Full-Color-Dark_j89hje.png' },
    { name:'UAI',        image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782504927/contact/landing/e6tylizrlwustq5zeydd.png' },
    { name:'LIVUP',      image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788835334/default-social-share-c-Photoroom_d7uuif.png' },
    { name:'Vambe',      image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534632/contact/landing/jvxdzazc3verc6raewgl.png' },
    { name:'',           image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534316/contact/landing/yjm1j1qo41cco55lgxvg.png' },
    { name:'Regulatek',  image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534470/contact/landing/uamro4aql49lhqwmakaq.png' },
    { name:'Tasky',      image_url:'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782504507/contact/landing/wia3gjwx5ro48uiyynfe.png' },
  ];

  const FEAT_CARDS = [
    { ...t.feat.cards[0], metric: 3, metricSuffix: '×' },
    { ...t.feat.cards[1], metric: 98, metricSuffix: '%' },
  ];

  const CAPS = [
    'Contacto rápido','Perfil digital','Wallet integrada','Sin aplicación',
    'Datos medibles','Leads identificados','Imagen profesional','Actualización instantánea',
  ];

  const SOCIAL_NETWORKS = [
    { name: 'Instagram', icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
    { name: 'LinkedIn',  icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
    { name: 'WhatsApp',  icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
    { name: 'Facebook',  icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
    { name: 'X',         icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg> },
    { name: 'YouTube',   icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
    { name: 'TikTok',    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> },
    { name: 'Telegram',  icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> },
    { name: 'Pinterest', icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg> },
    { name: 'Spotify',   icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg> },
    { name: 'GitHub',    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg> },
    { name: 'Behance',   icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M6.938 4.503c.702 0 1.34.06 1.92.188.577.13 1.07.33 1.485.61.41.28.733.65.96 1.12.225.47.34 1.05.34 1.73 0 .74-.17 1.36-.507 1.86-.338.5-.837.9-1.502 1.22.906.26 1.576.72 2.022 1.37.448.66.665 1.45.665 2.36 0 .75-.13 1.39-.41 1.93-.28.55-.67 1-1.16 1.35-.48.348-1.05.6-1.69.767-.63.17-1.29.254-1.96.254H0V4.502h6.938zm-.34 5.65c.585 0 1.05-.14 1.41-.42.35-.28.53-.71.53-1.3 0-.32-.06-.59-.18-.8-.12-.2-.29-.36-.5-.47-.21-.1-.44-.17-.7-.2-.27-.04-.54-.05-.82-.05H3.5v3.24h3.1zm.16 5.87c.31 0 .6-.03.87-.09s.5-.17.7-.33c.19-.15.35-.36.46-.62.11-.26.17-.59.17-.99 0-.79-.22-1.35-.64-1.68-.43-.33-.99-.5-1.69-.5H3.5v4.21h3.26zm8.71-5.5c.24-.37.57-.67.99-.91.42-.24.88-.4 1.39-.49.51-.09 1.02-.14 1.53-.14.46 0 .91.03 1.36.1.44.07.85.19 1.21.38.36.18.65.44.87.77.22.32.33.74.33 1.25v4.5c0 .39.02.77.06 1.12.04.35.1.61.19.77h-2.95c-.04-.13-.07-.26-.09-.4-.02-.14-.03-.28-.03-.42-.38.38-.82.65-1.32.82-.5.17-1.01.26-1.54.26-.4 0-.77-.05-1.12-.15-.34-.1-.64-.25-.89-.46-.25-.2-.45-.46-.59-.77-.14-.31-.21-.67-.21-1.1 0-.47.08-.86.24-1.17.16-.31.37-.56.63-.75.26-.19.56-.33.89-.43.33-.1.66-.17 1-.22l1.02-.14c.33-.04.62-.1.88-.17.25-.07.45-.17.59-.31.14-.13.21-.32.21-.57 0-.26-.04-.47-.13-.62-.09-.16-.21-.28-.37-.36-.15-.09-.33-.14-.53-.17-.2-.03-.42-.04-.64-.04-.5 0-.89.11-1.18.32-.28.22-.44.57-.49 1.07H15.5c.06-.52.19-.96.38-1.33zm4.1 3.29c-.14.05-.3.1-.48.13-.17.04-.36.07-.55.1l-.51.09c-.16.03-.31.08-.46.13-.14.06-.27.13-.37.22-.1.09-.19.2-.25.34-.07.14-.1.31-.1.51 0 .19.03.36.1.5.06.14.16.26.27.35.12.1.26.17.42.21.16.04.34.07.54.07.49 0 .88-.11 1.14-.32.27-.21.46-.46.57-.73.11-.28.17-.55.19-.83.01-.27.02-.5.02-.68v-.09c-.07.06-.14.1-.23.13zM14.6 5.32h5.3v1.47H14.6V5.32z"/></svg> },
  ];

  const PLANS = [
    {
      ...t.plans.list[0],
      price: '$0',
      priceYearly: '$0',
      priceSub: lang === 'en' ? 'forever' : lang === 'pt' ? 'para sempre' : 'para siempre',
      priceYearlySub: lang === 'en' ? 'forever' : lang === 'pt' ? 'para sempre' : 'para siempre',
      featured: false,
    },
    {
      ...t.plans.list[1],
      price: planPricing ? `$${planPricing.price_clp.toLocaleString('es-CL')}` : '$2.500',
      priceYearly: planPricing ? `$${planPricing.price_clp_annual.toLocaleString('es-CL')}` : '$25.000',
      priceSub: lang === 'en' ? '/mo · per profile' : lang === 'pt' ? '/mês · por perfil' : '/mes · por perfil',
      priceYearlySub: lang === 'en' ? '/yr · per profile · billed yearly' : lang === 'pt' ? '/ano · por perfil · cobrado anualmente' : '/año · por perfil · cobrado anual',
      featured: true,
    },
    {
      ...t.plans.list[2],
      contactSales: true,
      featured: false,
      ctaAction: 'agenda',
    },
  ];

  return (
    <div className="nx-root">

      {/* ── NAV ── */}
      <header className={`nx-nav ${navBorder ? 'nx-nav--scrolled' : ''}`}>
        <div className="nx-nav-inner">
          <div className="nx-nav-left">
            <button className="nx-hamburger" onClick={() => setMobileNavOpen(true)} aria-label="Abrir menú">
              <span/><span/><span/>
            </button>
            <ContactLogo clickable />
          </div>
          <nav className="nx-nav-links">
            <a href="#caracteristicas" className="nx-nav-link">{t.nav.features}</a>
            <a href="#precios"         className="nx-nav-link">{t.nav.plans}</a>
            <a href="#nosotros"        className="nx-nav-link">{t.nav.solution}</a>
            <a href="/app"             className="nx-nav-link">App</a>
          </nav>
          <div className="nx-nav-right">
            <LangSwitcher lang={lang} setLang={setLang} />
            {user ? (
              <div className="nx-account" ref={accountMenuRef}>
                <button className="nx-account-trigger" onClick={() => setAccountMenuOpen(v => !v)} aria-haspopup="true" aria-expanded={accountMenuOpen}>
                  <AccountAvatar className="nx-account-avatar" />
                  <span className="nx-account-name">{userFirstName}</span>
                  <IconChevronDown className={`nx-account-chevron ${accountMenuOpen ? 'nx-account-chevron--open' : ''}`} />
                </button>
                {accountMenuOpen && (
                  <div className="nx-account-menu">
                    <button className="nx-account-menu-item" onClick={() => goToAccountTab('profile')}>{t.nav.myProfile}</button>
                    <button className="nx-account-menu-item" onClick={() => goToAccountTab()}>{t.nav.dashboard}</button>
                    <button className="nx-account-menu-item" onClick={() => goToAccountTab('settings')}>{t.nav.settings}</button>
                    <div className="nx-account-menu-divider" />
                    <button className="nx-account-menu-item nx-account-menu-item--danger" onClick={handleLogout}>{t.nav.logout}</button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="nx-btn-ghost">{t.nav.login}</button>
                <button onClick={() => navigate('/login')} className="nx-btn-acceder">{t.nav.access} <IconArrowRight/></button>
              </>
            )}
            <button onClick={() => navigate('/comprar')} className="nx-btn-outline">{t.nav.buy}</button>
            <button onClick={openModal} className="nx-btn-pill">{t.nav.quote} <IconArrowRight/></button>
          </div>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="nx-mnav-overlay" onClick={() => setMobileNavOpen(false)}>
          <div className="nx-mnav-panel" onClick={e => e.stopPropagation()}>
            <div className="nx-mnav-head">
              <ContactLogo/>
              <button className="nx-mnav-close" onClick={() => setMobileNavOpen(false)} aria-label="Cerrar menú">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            {user && (
              <>
                <div className="nx-mnav-account">
                  <AccountAvatar className="nx-account-avatar" />
                  <span className="nx-mnav-account-name">{userFirstName}</span>
                </div>
                <button className="nx-mnav-link nx-mnav-link--button" onClick={() => goToAccountTab('profile')}>{t.nav.myProfile}</button>
                <button className="nx-mnav-link nx-mnav-link--button" onClick={() => goToAccountTab()}>{t.nav.dashboard}</button>
                <button className="nx-mnav-link nx-mnav-link--button" onClick={() => goToAccountTab('settings')}>{t.nav.settings}</button>
                <button className="nx-mnav-link nx-mnav-link--button nx-mnav-link--danger" onClick={handleLogout}>{t.nav.logout}</button>
                <div className="nx-mnav-divider" />
              </>
            )}
            <a href="#caracteristicas" className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>{t.nav.features}</a>
            <a href="#precios"         className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>{t.nav.plans}</a>
            <a href="#nosotros"        className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>{t.nav.solution}</a>
            <a href="/app"             className="nx-mnav-link" onClick={() => setMobileNavOpen(false)}>App</a>
            <LangSwitcher lang={lang} setLang={setLang} className="nx-mnav-lang-dropdown" />
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section className="nx-hero">
        <HeroDither/>
        {heroReady && (
        <div className="nx-container">
          <div className="nx-load" style={{animationDelay:'0ms'}}>
            <div className="nx-hero-badge" style={{visibility:'hidden'}} aria-hidden="true">
              {t.hero.trust}
            </div>
          </div>

          <div className="nx-load" style={{animationDelay:'130ms'}}>
            <h1 className="nx-hero-h1">
              <TextAnimate as="span" by="block" animation="blurInUp" duration={0.6} delay={0.1}>
                {t.hero.connectPre} <DiaText text={t.hero.touch} textColor="#C0C0C0" colors={['#C0C0C0', '#7dd3fc', '#0071e3']} duration={1.4} />.
              </TextAnimate>
              <TextAnimate as="span" by="block" animation="blurInUp" duration={0.6} delay={0.35}>
                {t.hero.measurePre} <DiaText text={t.hero.opportunity} textColor="#C0C0C0" colors={['#C0C0C0', '#7dd3fc', '#0071e3']} duration={1.4} delay={0.3} />.
              </TextAnimate>
            </h1>
          </div>

          <div className="nx-load" style={{animationDelay:'260ms'}}>
            <p className="nx-hero-sub">
              {t.hero.sub}
            </p>
          </div>

          <div className="nx-load" style={{animationDelay:'380ms'}}>
            <button onClick={() => navigate('/register')} className="nx-btn-pill nx-btn-pill--lg nx-btn-demo">
              <DiaText text={t.hero.cta} textColor="#fff" colors={['#ffffff', '#7dd3fc', '#0071e3']} duration={1.4} /> <IconArrowRight/>
            </button>
          </div>
        </div>
        )}
      </section>

      {/* ── CLIENTS (debajo del hero) ── */}
      {heroReady && (
        <Reveal delay={300} className="nx-hero-clients">
          <CustomersSection
            label={t.hero.trustLabel}
            customers={logos.map(l => ({ src: l.image_url, alt: l.name, height: 48 }))}
          />
        </Reveal>
      )}

      {/* ── VIDEO (debajo de las empresas clientes) ── */}
      <Reveal delay={200} className="nx-video-section">
        <div className="nx-container nx-video-wrap">
          <HoverPlayCard
            src="https://res.cloudinary.com/dfyzvch4i/video/upload/v1792626648/8BB91676-EF98-4DE2-80D1-BA01E8D781BA_uzaoqh.mov"
            poster="https://res.cloudinary.com/dfyzvch4i/video/upload/so_0/v1792626648/8BB91676-EF98-4DE2-80D1-BA01E8D781BA_uzaoqh.jpg"
          />
        </div>
      </Reveal>

      {/* ── PROBLEM STATEMENT ── */}
      <section className="nx-problem-section">
        <Reveal delay={160}>
        <div className="nx-container">
          <div className="nx-section-pre nx-section-pre--center">
            <h2 className="nx-section-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.problem.title}</h2>
            <p className="nx-section-sub">{t.problem.sub}</p>
          </div>
          <div className="nx-problem-grid">
            {t.problem.items.map((item, i) => (
              <div className="nx-problem-card" key={i}>
                <div className="nx-problem-stat">{item.stat}</div>
                <p className="nx-problem-desc">{item.desc}</p>
                <span className="nx-problem-icon">{PROBLEM_ICONS[i]}</span>
              </div>
            ))}
          </div>
          <p className="nx-problem-source">{t.problem.source}</p>
        </div>
        </Reveal>
      </section>

      {/* ── FEATURE CARDS ── */}
      <section className="nx-features" id="caracteristicas">
        <Reveal delay={320}>
        <div className="nx-container">
          <div className="nx-section-pre">
            <h2 className="nx-section-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>
              {t.feat.title1}<br/>
              {t.feat.title2}
            </h2>
          </div>

          <div className="nx-lc-photo">
            <img
              src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788743766/ChatGPT_Image_6_sept_2026_10_15_54_p.m._xumora.png"
              alt="Profesionales conectando en un evento"
              loading="lazy"
              width="1536"
              height="1024"
            />
          </div>

          <div className="nx-feat-grid">
            {FEAT_CARDS.map((fc, i) => (
              <div key={i}>
                <div className={`nx-feat-card${i === 1 ? ' nx-feat-card--glow' : ''}`}>
                  {i === 1 && (<>
                    <HorizonGlowBg/>
                    <div className="nx-feat-glow-scrim"/>
                  </>)}
                  <div className="nx-feat-card-top">
                    <h3 className="nx-feat-title">{fc.headline}</h3>
                    <p className="nx-feat-body">{fc.body}</p>
                  </div>
                  <div className="nx-feat-card-bottom">
                    <div className="nx-feat-metric"><span><NumberTicker value={fc.metric}/>{fc.metricSuffix}</span><span className="nx-feat-metric-label">{fc.metricLabel}</span></div>
                    <button onClick={openModal} className="nx-feat-cta">{t.feat.cta} <IconArrowRight/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{textAlign:'center', marginTop:32}}>
            <button onClick={() => navigate('/tarjeta-digital')} className="nx-btn-outline">
              Ver más información <IconArrowRight/>
            </button>
          </div>
        </div>
        </Reveal>
      </section>

      {/* ── LEAD CAPTURE + DIGITAL BUSINESS CARD ── */}
      <LeadCaptureShowcase lang={lang}/>

      {/* ── DARK CAPABILITIES ── */}
      {/* ── FEATURE CAROUSEL ── */}
      <ProductShowcase lang={lang}/>

      {/* ── HOW IT WORKS ── */}
      <section className="nx-howit">
        <Reveal delay={200}>
        <div className="nx-container">
          <div className="nx-howit-grid">
            {t.howit.map((step, i) => (
              <div className="nx-howit-step" key={i}>
                <span className="nx-howit-num">{String(i+1).padStart(2,'0')}</span>
                <h3 className="nx-howit-title">{step.title}</h3>
                <p className="nx-howit-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
        </Reveal>
      </section>

      {/* ── API E INTEGRACIONES ── */}
      <section className="nx-api-section">
        <Reveal delay={120}>
        <div className="nx-container">
          <div className="nx-api-grid">
            <div className="nx-showcase-visual"><ApiTerminalMockup/></div>
            <div>
              <span className="nx-api-eyebrow">{t.api.eyebrow}</span>
              <h2 className="nx-showcase-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>
                {t.api.title1}<br/>{t.api.title2}
              </h2>
              <p className="nx-showcase-body">{t.api.sub}</p>
              <button onClick={() => navigate('/docs')} className="nx-btn-pill">{t.api.cta} <IconArrowRight/></button>
            </div>
          </div>
        </div>
        </Reveal>
      </section>

      {/* ── BENTO: SEGURIDAD + INTEGRACIONES + ANALYTICS + WALLET ── */}
      <section className="nx-bento-section">
        <Reveal delay={160}>
        <div className="nx-container">
          <div className="nx-bento-grid">

            {/* A — Seguridad */}
            <div className="nx-bento-card nx-bento-a">
              <div className="nx-bento-a-head">
                <h3 className="nx-bento-heading">{t.security.title1} {t.security.title2}</h3>
                <p className="nx-bento-sub">{t.security.sub}</p>
              </div>
              <div className="nx-bento-a-grid">
                {t.security.items.map((item, i) => (
                  <div className="nx-bento-a-item" key={i}>
                    <span className="nx-bento-a-icon">{SECURITY_ICONS[i]}</span>
                    <h4 className="nx-bento-a-item-title">{item.title}</h4>
                    <p className="nx-bento-a-item-desc">{item.desc}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate('/seguridad')} className="nx-btn-outline" style={{marginTop:18}}>
                Ver más información <IconArrowRight/>
              </button>
            </div>

            {/* B — Analytics */}
            <div className="nx-bento-card nx-bento-b">
              <p className="nx-bento-center-heading">{t.bento.analyticsTitle}</p>
              <button onClick={() => navigate('/analytics')} className="nx-btn-outline" style={{margin:'0 auto 16px', display:'flex'}}>
                Ver más información <IconArrowRight/>
              </button>
              <div className="nx-bento-b-imgwrap">
                <img
                  src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1785534487/Captura_de_pantalla_2026-07-31_a_la_s_5.47.27_p.m._wqsdti.png"
                  alt="Analytics de CONTACT"
                  className="nx-bento-b-img"
                  loading="lazy"
                  width="2816"
                  height="1588"
                />
              </div>
            </div>

            {/* C — Wallet */}
            <div className="nx-bento-card nx-bento-c">
              <p className="nx-bento-center-heading">{t.bento.walletTitle}</p>
              <div className="nx-bento-c-imgwrap">
                <img
                  src="https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1785814169/CONTACT_WALLET_psttct.png"
                  alt="CONTACT en Apple Wallet y Google Wallet"
                  className="nx-bento-c-img"
                  loading="lazy"
                  width="1122"
                  height="1402"
                />
              </div>
            </div>

            {/* D — Integraciones */}
            <div className="nx-bento-card nx-bento-d">
              <div className="nx-bento-d-head">
                <h3 className="nx-bento-heading">{t.integrations.title1} {t.integrations.title2}</h3>
                <p className="nx-bento-sub">{t.integrations.sub}</p>
              </div>
              <div className="nx-bento-d-grid">
                <div className="nx-bento-d-box nx-bento-d-box--logo"><OrbitBrandIcon color="#34A853" d={ORBIT_ICON_D.sheets}/></div>
                <div className="nx-bento-d-box nx-bento-d-box--logo"><OrbitBrandIcon color="#FF7A59" d={ORBIT_ICON_D.hubspot}/></div>
                <div className="nx-bento-d-box nx-bento-d-box--logo"><img src="/salesforce-logo.png" alt="Salesforce" className="nx-bento-d-salesforce"/></div>
              </div>
            </div>

          </div>
        </div>
        </Reveal>
      </section>

      {/* ── PLANS ── */}
      <section className="nx-plans" id="precios" style={{position:'relative'}}>
        <div style={{position:'absolute',left:0,right:0,top:'-80px',bottom:0,overflow:'hidden',pointerEvents:'none',zIndex:0}}>
          <Meteors number={18}/>
        </div>
        <div className="nx-container" style={{position:'relative',zIndex:1}}>
          <Reveal delay={320}>
          <div className="nx-section-pre nx-section-pre--center">
            <h2 className="nx-section-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.plans.title}</h2>
            <p className="nx-section-sub">{t.plans.sub}</p>
            <div className="nx-billing-toggle">
              <button
                className={`nx-billing-opt${billing==='monthly'?' nx-billing-opt--on':''}`}
                onClick={() => handleSetBilling('monthly')}
              >{t.plans.monthly}</button>
              <button
                ref={billingYearlyRef}
                className={`nx-billing-opt${billing==='yearly'?' nx-billing-opt--on':''}`}
                onClick={() => handleSetBilling('yearly')}
              >{t.plans.yearly} <span className="nx-billing-save">{t.plans.save}</span></button>
            </div>
          </div>
          <div className="nx-plans-mobile-tabs">
            {PLANS.map((plan, i) => (
              <button
                key={i}
                className={`nx-plans-mobile-tab${mobilePlanTab===i?' nx-plans-mobile-tab--on':''}`}
                onClick={() => setMobilePlanTab(i)}
              >{plan.name}</button>
            ))}
          </div>
          <div className="nx-plans-grid">
            {PLANS.map((plan, i) => {
              const activePrice  = billing === 'yearly' && plan.priceYearly ? plan.priceYearly : plan.price;
              const activeSub    = billing === 'yearly' && plan.priceYearlySub ? plan.priceYearlySub : plan.priceSub;
              return (
              <Reveal
                key={i}
                className={mobilePlanTab===i ? 'nx-plan-tab-active' : 'nx-plan-tab-inactive'}
                delay={i * 120}
              >
                <div className={`nx-plan ${plan.featured ? 'nx-plan--featured' : ''}`}>
                  {plan.featured && <span className="nx-border-beam" aria-hidden="true"/>}
                  {plan.featured && <div className="nx-plan-badge">{t.plans.recommended}</div>}
                  <div className="nx-plan-name">{plan.name}</div>
                  <p className="nx-plan-desc">{plan.desc}</p>
                  <div className="nx-plan-price-wrap">
                    {plan.contactSales ? (
                      <div className="nx-plan-price-contact">
                        <div className="nx-plan-price-contact-main">{t.plans.contactSales}</div>
                        <p className="nx-plan-price-contact-sub">{plan.priceTagline}</p>
                      </div>
                    ) : (
                      <>
                        {plan.priceOld && billing === 'monthly' && (
                          <div className="nx-plan-price-old-row">
                            <span className="nx-plan-price-old">{plan.priceOld}</span>
                            <span className="nx-plan-price-note">{plan.priceNote}</span>
                          </div>
                        )}
                        <div className="nx-plan-price-row">
                          <span className="nx-plan-price-main nx-plan-price-anim" key={activePrice}>{formatPrice(activePrice)}</span>
                          <span className="nx-plan-price-sub">{activeSub}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <ul className="nx-plan-list">
                    {plan.features.map((f, j) => (
                      <li key={j} className="nx-plan-li">
                        <span className="nx-li-check"><IconCheck/></span>{f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={
                      plan.ctaAction === 'agenda' ? () => navigate('/agenda-demo') :
                      plan.ctaAction === 'quote' ? openModal :
                      () => navigate('/register')
                    }
                    className={plan.featured ? 'nx-btn-pill nx-plan-btn' : 'nx-btn-outline nx-plan-btn'}
                  >
                    {plan.cta}
                  </button>
                </div>
              </Reveal>
              );
            })}
          </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAN CAROUSEL ── */}
      <section className="nx-fan-section">
        <Reveal delay={160}>
        <div className="nx-container">
          <div className="nx-fan3-grid">
            <div>
              <h2 className="nx-showcase-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.fan.title}</h2>
              <p className="nx-showcase-body">{t.fan.sub}</p>
            </div>
            <StraightProfileCards cards={FAN3_PROFILES} />
          </div>
        </div>
        </Reveal>
      </section>

      {/* ── GLOBE CTA ── */}
      <section className="nx-globe-section">
        <Reveal delay={180}>
        <div className="nx-container">
          <div className="nx-globe-card">
            <div className="nx-globe-text">
              <h2 className="nx-globe-h2">
                <span className="nx-globe-h2-dark">{t.globeCta.dark}</span> <span className="nx-globe-h2-light">{t.globeCta.light}</span>
              </h2>
              <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                <button onClick={() => navigate('/register')} className="nx-btn-pill">
                  {t.globeCta.cta} <IconArrowRight/>
                </button>
                <button onClick={() => navigate('/wallet')} className="nx-btn-outline">
                  Ver más información
                </button>
              </div>
            </div>
            <div className="nx-globe-visual">
              <GlobeCanvas/>
            </div>
          </div>
        </div>
        </Reveal>
      </section>

      {/* ── SOCIAL INTEGRATIONS ── */}
      <section className="nx-social-strip">
        <Reveal delay={320}>
        <div className="nx-container">
          <div className="nx-clients-mask">
            <div className="nx-social-track">
              {[...SOCIAL_NETWORKS,...SOCIAL_NETWORKS,...SOCIAL_NETWORKS].map((sn,i)=>(
                <div key={i} className="nx-social-item">
                  <div className="nx-social-icon">{sn.icon}</div>
                  <span className="nx-social-name">{sn.name}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="nx-social-more">{t.social.more}</p>
        </div>
        </Reveal>
      </section>

      {/* ── FAQ ── */}
      <section className="nx-faq" id="faq">
        <div className="nx-container">
          <Reveal delay={320}>
          <div className="nx-section-pre nx-section-pre--center">
            <h2 className="nx-section-h2" style={{fontFamily:"'abcNormal','Inter','Arial',sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.faq.title}</h2>
            <p className="nx-section-sub">{t.faq.sub}</p>
          </div>
          <div className="nx-faq-list">
            {t.faq.items.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <Suspense fallback={null}>
      <RuixenGradientFooter className="nx-footer" gradientHeight={typeof window !== 'undefined' && window.innerWidth < 640 ? '10vh' : '18vh'}>
        <div className="nx-container">

          <div className="nx-footer-grid">
            <div>
              <div className="nx-footer-socials">
                <a href="https://instagram.com/contact_cl" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="nx-footer-social">
                  <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://www.linkedin.com/company/contactnetworkcl" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="nx-footer-social">
                  <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <div className="nx-footer-col-title">{t.footer.product}</div>
              <div className="nx-footer-links">
                <a href="#caracteristicas">{t.footer.features}</a>
                <a href="#precios">{t.footer.plans}</a>
                <button onClick={() => navigate('/docs')} className="nx-footer-link-btn">{t.footer.docs}</button>
                <button onClick={() => navigate('/agenda-demo')} className="nx-footer-link-btn">{t.footer.schedule}</button>
              </div>
            </div>
            <div>
              <div className="nx-footer-col-title">{t.footer.company}</div>
              <div className="nx-footer-links">
                <a href="/sobre-nosotros">{t.footer.about}</a>
                <a href="mailto:soporte@contactnetwork.cl">soporte@contactnetwork.cl</a>
                <a href="tel:+56982036712">+56 9 8203 6712</a>
              </div>
            </div>
            <div>
              <div className="nx-footer-col-title">{t.footer.legal}</div>
              <div className="nx-footer-links">
                <button onClick={() => navigate('/terminos')} className="nx-footer-link-btn">{t.footer.terms}</button>
                <button onClick={() => navigate('/privacidad')} className="nx-footer-link-btn">{t.footer.privacy}</button>
                <button onClick={() => navigate('/tratamiento-de-datos')} className="nx-footer-link-btn">{t.footer.dataProcessing}</button>
                <span className="nx-footer-detail">{t.footer.hours}</span>
              </div>
            </div>
            <div>
              <button className="nx-footer-sec-btn" onClick={() => setShowSecurity(s => !s)}>
                <span className="nx-footer-col-title" style={{margin:0}}>{t.footer.security}</span>
                <svg className={'nx-footer-sec-chevron' + (showSecurity ? ' nx-footer-sec-open' : '')} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              {showSecurity && (
                <p className="nx-footer-sec-text">{t.footer.securityText}</p>
              )}
            </div>
          </div>
          <div className="nx-footer-bottom">
            <span style={{fontFamily:"'Inter',system-ui,sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.footer.copyright}</span>
            <select
              className="nx-currency-select"
              value={currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              aria-label="Moneda de referencia para los precios"
            >
              {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span>{t.footer.tagline}</span>
          </div>
        </div>
      </RuixenGradientFooter>
      </Suspense>

      {showQuotationModal && (
        <QuotationModal
          onClose={closeModal}
          lang={lang}
          quotationData={quotationData} setQuotationData={setQuotationData}
          quotationSent={quotationSent} isSubmitting={isSubmitting}
          priceRanges={priceRanges}
          quantityType={quantityType} setQuantityType={setQuantityType}
          customQuantity={customQuantity} setCustomQuantity={setCustomQuantity}
          quantityError={quantityError} setQuantityError={setQuantityError}
          handleQuotationSubmit={handleQuotationSubmit}
        />
      )}
      {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}
      {showPrivacyModal && <PrivacyModal onClose={() => setShowPrivacyModal(false)} />}
      {showDataProcessingModal && <DataProcessingModal onClose={() => setShowDataProcessingModal(false)} />}
      <style>{CSS}</style>
    </div>
  );
});

/* ─── QUOTATION MODAL ────────────────────────────────────── */
const QuotationModal = ({ onClose, lang, quotationData, setQuotationData, quotationSent, isSubmitting, priceRanges, quantityType, setQuantityType, customQuantity, setCustomQuantity, quantityError, setQuantityError, handleQuotationSubmit }) => {
  const t = TR[lang].quote;
  return (
  <div className="qm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="qm-modal">
      <div className="qm-head">
        <div><h2>{t.title}</h2><p>{t.sub}</p></div>
        <button className="qm-x" onClick={onClose} aria-label="Cerrar">
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="qm-body">
        {quotationSent ? (
          <div className="qm-success">
            <div className="qm-success-ico">
              <svg width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
            </div>
            <h3>{t.successTitle}</h3>
            <p>{t.successBody}</p>
          </div>
        ) : (
          <form onSubmit={handleQuotationSubmit}>
            <div className="qm-field"><label>{t.company} <span className="qm-req">*</span></label><input type="text" required value={quotationData.company} onChange={e => setQuotationData({...quotationData, company: e.target.value})} placeholder={t.companyPh} /></div>
            <div className="qm-row">
              <div className="qm-field"><label>{t.contactPerson} <span className="qm-req">*</span></label><input type="text" required value={quotationData.contact_person} onChange={e => setQuotationData({...quotationData, contact_person: e.target.value})} placeholder={t.contactPersonPh} /></div>
              <div className="qm-field"><label>{t.email} <span className="qm-req">*</span></label><input type="email" required value={quotationData.email} onChange={e => setQuotationData({...quotationData, email: e.target.value})} placeholder={t.emailPh} /></div>
            </div>
            <div className="qm-row">
              <div className="qm-field"><label>{t.phone} <span className="qm-req">*</span></label><input type="tel" required value={quotationData.phone} onChange={e => setQuotationData({...quotationData, phone: e.target.value})} placeholder={t.phonePh} /></div>
              <div className="qm-field">
                <label>{t.estimated} <span className="qm-req">*</span></label>
                {quantityType === 'preset' ? (
                  <select required value={quotationData.estimated_cards} onChange={e => setQuotationData({...quotationData, estimated_cards: e.target.value})}>
                    <option value="">{t.selectRange}</option>
                    {priceRanges.map((r, i) => <option key={i} value={r.range}>{r.description}</option>)}
                  </select>
                ) : (
                  <div className="qm-qty-row">
                    <button type="button" className="qm-qty-btn" onClick={() => { const v = Math.max(1, customQuantity-1); setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); }}>−</button>
                    <input type="number" min="1" value={customQuantity} className="qm-qty-input" onChange={e => { const v = parseInt(e.target.value)||1; setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); setQuantityError(v<1?t.min:''); }} />
                    <button type="button" className="qm-qty-btn" onClick={() => { const v = customQuantity+1; setCustomQuantity(v); setQuotationData({...quotationData, estimated_cards: String(v)}); }}>+</button>
                  </div>
                )}
                <div className="qm-qty-toggle">
                  <label><input type="radio" value="preset" checked={quantityType==='preset'} onChange={() => { setQuantityType('preset'); setQuantityError(''); setQuotationData({...quotationData, estimated_cards: ''}); }} /> {t.ranges}</label>
                  <label><input type="radio" value="custom" checked={quantityType==='custom'} onChange={() => { setQuantityType('custom'); setQuotationData({...quotationData, estimated_cards: String(customQuantity)}); }} /> {t.exact}</label>
                </div>
                {quantityError && <p className="qm-error">{quantityError}</p>}
              </div>
            </div>
            <div className="qm-field"><label>{t.description}</label><textarea rows="3" value={quotationData.comments} onChange={e => setQuotationData({...quotationData, comments: e.target.value})} placeholder={t.descriptionPh} /></div>
            <button type="submit" disabled={isSubmitting} className="qm-submit">{isSubmitting ? t.sending : t.submit}</button>
          </form>
        )}
      </div>
    </div>
  </div>
  );
};

/* ─── LEGAL MODALS ───────────────────────────────────────── */
// Terms/Privacy/Data Processing text is Spanish-only and lives in
// LEGAL_ES (constants/legalContent.js) — single source of truth shared
// with RegisterPage.js and ComprarPage.js, so it can never drift between
// pages. Not translated per-language on purpose (legal text needs a
// careful, reviewed translation, not a marketing-copy one).
const LegalModal = ({ data, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">{data.title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg></button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 text-gray-700 text-sm leading-relaxed space-y-6">
        <div className="text-center mb-6"><h3 className="text-lg font-bold text-gray-900">{data.heading}</h3></div>
        {data.sections.map(([title, body], i) => (
          <section key={i}>
            <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
            {body.split('\n\n').map((p, j) => <p key={j} className={j > 0 ? 'mt-2' : ''}>{p}</p>)}
          </section>
        ))}
      </div>
      <div className="p-4 border-t border-gray-200">
        <button onClick={onClose} className="w-full py-3 bg-[#111] text-white font-semibold rounded-lg hover:bg-black transition-all">{data.done}</button>
      </div>
    </div>
  </div>
);

const TermsModal = ({ onClose }) => <LegalModal data={LEGAL_ES.terms} onClose={onClose} />;
const PrivacyModal = ({ onClose }) => <LegalModal data={LEGAL_ES.privacy} onClose={onClose} />;
const DataProcessingModal = ({ onClose }) => <LegalModal data={LEGAL_ES.dataProcessing} onClose={onClose} />;

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `

@font-face {
  font-family: 'abcNormal';
  src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
  /* optional (not swap): the font is preloaded in index.html, so it's ready
     in time in the common case. If it isn't, we keep the fallback for this
     page view instead of swapping mid-animation on the hero's large H1 —
     that swap was causing a visible reflow right as the hero's entrance
     animation played. */
  font-display: optional;
}

/* ── TOKENS ── */
:root{
  --bg:#F9F9F8;
  --bg-w:#FFFFFF;
  --text:#000000;
  --muted:#4a4a4a;
  --faint:#C0C0C0;
  --border:#EEEEEC;
  --border-dk:#CCCCCA;
  --dark:#0F172A;
  --max:1200px;
  --r:10px;
}
*{box-sizing:border-box;margin:0;padding:0}
html{-webkit-overflow-scrolling:touch}
@keyframes nxAppFadeIn{from{opacity:0}to{opacity:1}}
.nx-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--bg);color:var(--text);overflow-x:hidden;line-height:1.6;animation:nxAppFadeIn .3s ease both}
button{font-family:'Inter',system-ui,sans-serif!important}
.nx-container{max-width:var(--max);margin:0 auto;padding:0 40px}

/* ── WIDE MONITORS ── */
/* Below ~1600px (laptops, incl. Retina MacBooks up to 1728 CSS px) the page
   already looks right at --max:1200px. On genuinely wide external monitors
   the container stopped growing at 1200px, leaving large empty side gutters
   — this scales the container (and the nav/plans grids, which don't use the
   --max token) up together past that point. */
@media (min-width: 1600px) {
  :root{--max:1440px}
  .nx-nav{max-width:1400px}
  .nx-plans-grid{max-width:1420px}
}

/* ── REVEAL ── */
.nx-reveal{opacity:0;transform:translateY(24px) scale(.97);transition:opacity .5s cubic-bezier(.16,1,.3,1),transform .5s cubic-bezier(.16,1,.3,1);will-change:opacity,transform}
.nx-reveal.nx-visible{opacity:1;transform:translateY(0) scale(1)}
.nx-reveal--left{transform:translateX(-24px) scale(.975)}
.nx-reveal--left.nx-visible{transform:translateX(0) scale(1)}
.nx-reveal--right{transform:translateX(24px) scale(.975)}
.nx-reveal--right.nx-visible{transform:translateX(0) scale(1)}

/* ── KEYFRAMES ── */
@keyframes logoScroll{0%{transform:translateX(0)}100%{transform:translateX(-33.333%)}}
@keyframes tickerScroll{0%{transform:translateX(0)}100%{transform:translateX(-33.333%)}}
@keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes nxDarkFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes nxDarkGlow{0%,100%{box-shadow:0 24px 72px rgba(0,0,0,.45),0 0 0 1px rgba(255,255,255,.04),0 0 80px rgba(59,130,246,.08)}50%{box-shadow:0 36px 88px rgba(0,0,0,.55),0 0 0 1px rgba(255,255,255,.07),0 0 140px rgba(59,130,246,.22)}}
@keyframes nxLoadIn{from{opacity:0;transform:translateY(40px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes nxMeteor{0%{transform:rotate(215deg) translateX(0);opacity:1}70%{opacity:1}100%{transform:rotate(215deg) translateX(-500px);opacity:0}}
.nx-meteor{pointer-events:none;position:absolute;width:2px;height:2px;border-radius:9999px;background:#0071e3;box-shadow:0 0 0 1px rgba(0,113,227,.15);animation:nxMeteor linear infinite}
.nx-meteor::before{content:'';position:absolute;top:50%;transform:translateY(-50%);width:60px;height:1px;background:linear-gradient(to right,rgba(0,113,227,.6),transparent)}
@keyframes nxNavIn{from{opacity:0;transform:translateY(-14px)}to{opacity:1;transform:translateY(0)}}

/* ── BUTTONS ── */
.nx-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--text);color:#fff;border:none;border-radius:100px;height:46px;padding:0 22px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .15s;white-space:nowrap}
.nx-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.nx-btn-pill--lg{height:52px;font-size:15px;padding:0 28px}
.nx-btn-pill--white{background:#fff;color:var(--text)}
.nx-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--text);border:1.5px solid var(--border-dk);border-radius:100px;height:46px;padding:0 22px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.nx-btn-outline:hover{border-color:var(--text);background:var(--bg-w)}
.nx-btn-ghost{background:none;border:none;color:var(--muted);font-family:inherit;font-size:14px;font-weight:500;cursor:pointer;padding:0 12px;height:44px;transition:color .15s}
.nx-btn-ghost:hover{color:var(--text)}
.nx-btn-acceder{display:none}
.nx-lang-dropdown{position:relative}
.nx-lang-btn{display:inline-flex;align-items:center;justify-content:center;gap:5px;background:none;border:1px solid var(--border-dk);color:var(--text);border-radius:100px;height:36px;padding:0 12px;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;letter-spacing:.01em}
.nx-lang-btn:hover{background:var(--bg-w);border-color:var(--text)}
.nx-lang-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1px solid var(--border);border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,.14);padding:4px;min-width:140px;z-index:50}
.nx-lang-menu-item{display:block;width:100%;text-align:left;padding:9px 12px;border:none;background:none;border-radius:8px;font-size:13px;color:var(--text);cursor:pointer;font-family:inherit;transition:background .15s}
.nx-lang-menu-item:hover{background:var(--bg-w)}
.nx-lang-menu-item--active{font-weight:700}
.nx-mnav-lang-dropdown{margin-top:8px}
.nx-mnav-lang-dropdown .nx-lang-btn{width:100%;justify-content:space-between;padding:0 14px}
.nx-mnav-lang-dropdown .nx-lang-menu{left:0;right:0;min-width:0}

/* ── TAG ── */
.nx-tag{display:inline-flex;align-items:center;background:var(--text);color:#fff;border-radius:100px;padding:5px 13px;font-size:12px;font-weight:600;white-space:nowrap}
.nx-eyebrow{display:block;font-size:12px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.07em;margin-bottom:14px}

/* ── NAV ── */
.nx-nav{position:fixed;top:16px;left:16px;right:16px;z-index:100;max-width:1160px;margin:0 auto;background:rgba(255,255,255,.6);backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);border:1px solid rgba(255,255,255,.5);border-radius:20px;box-shadow:0 4px 24px rgba(0,0,0,.05);transition:background .3s,box-shadow .3s,border-color .3s,top .3s;transform:translateZ(0);will-change:transform}
.nx-nav--scrolled{background:rgba(255,255,255,.82);border-color:rgba(0,0,0,.06);box-shadow:0 8px 28px rgba(0,0,0,.09)}
.nx-nav-inner{max-width:100%;margin:0 auto;padding:0 22px;height:50px;display:flex;align-items:center;justify-content:space-between}
.nx-nav-left{display:flex;align-items:center;gap:14px}
.nx-nav-left .h-8{height:26px}
.nx-hamburger{display:none;flex-direction:column;justify-content:center;align-items:center;gap:4px;width:30px;height:30px;background:none;border:none;cursor:pointer;padding:0;flex-shrink:0}
.nx-hamburger span{display:block;width:17px;height:2px;border-radius:2px;background:var(--text)}
.nx-nav-links{display:flex;gap:24px}
.nx-nav-link{font-size:13px;font-weight:500;color:var(--muted);text-decoration:none;transition:color .15s}
.nx-nav-link:hover{color:var(--text)}
.nx-nav-right{display:flex;align-items:center;gap:6px}
.nx-nav-right .nx-btn-pill,.nx-nav-right .nx-btn-outline{height:36px;padding:0 16px;font-size:13px}
.nx-btn-ghost{height:36px;padding:0 10px;font-size:13px}

/* ── ACCOUNT MENU (logged-in header) ── */
.nx-account{position:relative}
.nx-account-trigger{display:flex;align-items:center;gap:8px;background:none;border:1px solid transparent;border-radius:100px;height:36px;padding:0 8px 0 0;cursor:pointer;font-family:inherit;transition:background .15s,border-color .15s}
.nx-account-trigger:hover{background:var(--bg-w);border-color:var(--border)}
.nx-account-avatar{display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:var(--text);color:#fff;font-size:12.5px;font-weight:700;letter-spacing:0;flex-shrink:0}
.nx-account-avatar--img{object-fit:cover}
.nx-account-name{font-size:13.5px;font-weight:600;color:var(--text);white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis}
.nx-account-chevron{color:var(--muted);transition:transform .18s ease;flex-shrink:0}
.nx-account-chevron--open{transform:rotate(180deg)}
.nx-account-menu{position:absolute;top:calc(100% + 10px);right:0;min-width:188px;background:#fff;border:1px solid var(--border);border-radius:14px;box-shadow:0 16px 40px rgba(0,0,0,.12);padding:6px;z-index:60;animation:nxAccountMenuIn .15s ease both}
@keyframes nxAccountMenuIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
.nx-account-menu-item{display:block;width:100%;text-align:left;background:none;border:none;font-family:inherit;font-size:13.5px;font-weight:500;color:var(--text);cursor:pointer;padding:9px 12px;border-radius:9px;transition:background .12s}
.nx-account-menu-item:hover{background:#F5F5F7}
.nx-account-menu-item--danger{color:#DC2626}
.nx-account-menu-divider{height:1px;background:var(--border);margin:6px 4px}

/* ── MOBILE NAV DRAWER ── */
.nx-mnav-overlay{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.45);backdrop-filter:blur(2px);display:flex;animation:nxMnavFade .2s ease}
.nx-mnav-panel{width:78%;max-width:300px;height:100%;background:#fff;box-shadow:4px 0 32px rgba(0,0,0,.16);display:flex;flex-direction:column;padding:24px 22px;animation:nxMnavSlide .28s cubic-bezier(.22,1,.36,1)}
.nx-mnav-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:32px}
.nx-mnav-close{width:32px;height:32px;border-radius:8px;background:rgba(0,0,0,.05);border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--text);flex-shrink:0}
.nx-mnav-link{font-size:17px;font-weight:600;color:var(--text);text-decoration:none;padding:16px 4px;border-bottom:1px solid var(--border)}
.nx-mnav-link:last-child{border-bottom:none}
.nx-mnav-link--button{display:block;width:100%;text-align:left;background:none;border:none;font-family:inherit;cursor:pointer;border-bottom:1px solid var(--border)}
.nx-mnav-link--danger{color:#DC2626}
.nx-mnav-account{display:flex;align-items:center;gap:12px;padding:4px 4px 20px}
.nx-mnav-account .nx-account-avatar{width:40px;height:40px;font-size:15px}
.nx-mnav-account-name{font-size:16px;font-weight:700;color:var(--text)}
.nx-mnav-divider{height:1px;background:var(--border);margin:4px 0 16px}
@keyframes nxMnavFade{from{opacity:0}to{opacity:1}}
@keyframes nxMnavSlide{from{transform:translateX(-100%)}to{transform:translateX(0)}}

/* ── HERO ── */
.nx-hero{padding:136px 0 0;text-align:center}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}
.nx-hero-clients{margin-top:52px;padding:28px 0 14px}
.nx-hero-clients .nx-clients-label{margin-bottom:16px}
.nx-video-section{padding:40px 0 60px;position:relative;z-index:1}
.nx-video-wrap video{display:block;width:100%}
.nx-hero-badge{display:inline-flex;align-items:center;gap:8px;border:1px solid var(--border);border-radius:100px;padding:7px 16px;font-size:13px;color:var(--muted);background:var(--bg-w);margin-bottom:32px}
.nx-badge-dot{width:7px;height:7px;border-radius:50%;background:#22C55E;flex-shrink:0}
.nx-hero-h1{font-family:'Inter',system-ui,sans-serif;font-size:clamp(48px,6.5vw,88px);font-weight:300;line-height:1.06;letter-spacing:-.025em;color:var(--text);margin-bottom:24px}
.nx-muted{color:var(--faint)}
.nx-hero-sub{font-size:18px;color:#000;line-height:1.65;max-width:480px;margin:0 auto 36px}


/* ── CLIENTS ── */
.nx-clients{padding:48px 0}
.nx-clients-label{font-size:14px;font-weight:500;color:var(--muted);text-align:center;margin-bottom:20px;letter-spacing:-.01em}
.nx-customers-grid{display:flex;flex-wrap:wrap;justify-content:center;gap:32px 48px;max-width:960px;margin:0 auto;align-items:center}
.nx-customers-logo{display:flex;justify-content:center;align-items:center;flex:0 0 148px}
.nx-customers-logo img{filter:grayscale(1) brightness(0.35);opacity:.75;transition:opacity .2s}
.nx-customers-logo img:hover{opacity:1}
@keyframes nxLogoIn{from{opacity:0;filter:blur(12px);transform:translateY(12px)}to{opacity:1;filter:blur(0);transform:translateY(0)}}
.nx-cust-item{animation:nxLogoIn 1.5s cubic-bezier(.34,1.56,.64,1) both;animation-delay:calc(var(--nx-i,0)*80ms + 100ms)}
.nx-clients-mask{overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent);mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)}
.nx-social-strip{padding:90px 0 52px}
.nx-social-track{display:flex;width:max-content;animation:tickerScroll 24s linear infinite}
.nx-social-item{flex-shrink:0;padding:0 28px;display:flex;flex-direction:column;align-items:center;gap:8px}
.nx-social-icon{width:32px;height:32px;display:flex;align-items:center;justify-content:center;color:#000;opacity:.6;filter:grayscale(1);transition:opacity .2s}
.nx-social-item:hover .nx-social-icon{opacity:.9}
.nx-social-name{font-size:11px;font-weight:500;color:var(--faint);letter-spacing:.02em;white-space:nowrap;text-transform:uppercase}
.nx-social-more{text-align:center;margin-top:22px;font-size:13px;color:var(--faint);font-weight:400;letter-spacing:-.01em;font-style:italic;font-family:Georgia,'Times New Roman',serif}

/* ── FEATURES ── */
.nx-features{padding:50px 0 40px;background:transparent}
.nx-section-pre{margin-bottom:56px}
.nx-section-pre--center{text-align:center}
.nx-section-pre--center .nx-section-sub{margin:0 auto}
.nx-section-h2{font-size:clamp(30px,3.5vw,48px);font-weight:800;line-height:1.1;letter-spacing:-.03em;color:var(--text);margin-bottom:14px}
.nx-section-sub{font-size:17px;color:#000;line-height:1.65;max-width:520px}
.nx-feat-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.nx-feat-card{position:relative;overflow:hidden;background:rgba(255,255,255,.82);border:1px solid var(--border);border-radius:6px;padding:32px;display:flex;flex-direction:column;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);user-select:none;cursor:default;height:100%}
.nx-feat-card--glow{background:transparent}
.nx-feat-glow{position:absolute;inset:0;z-index:0;background-color:#EAF4FF;pointer-events:none}
.nx-feat-glow-scrim{position:absolute;inset:0;z-index:1;background:rgba(255,255,255,.55);pointer-events:none}
.nx-feat-card--glow .nx-feat-card-top,.nx-feat-card--glow .nx-feat-card-bottom{position:relative;z-index:2}
.nx-feat-card--glow .nx-feat-card-bottom{border-top:none}
.nx-feat-card-top{flex:1}
.nx-feat-badge{display:inline-block;border:1px solid var(--border-dk);border-radius:100px;padding:4px 12px;font-size:11px;font-weight:600;color:var(--muted);margin-bottom:18px}
.nx-feat-title{font-size:clamp(20px,2.2vw,26px);font-weight:400;line-height:1.25;letter-spacing:-.025em;color:var(--text);margin-bottom:12px}
.nx-feat-body{font-size:15px;color:#000;line-height:1.7;margin-bottom:20px}
.nx-feat-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:28px}
.nx-feat-card-bottom{border-top:1px solid var(--border);padding-top:20px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px}
.nx-feat-metric{font-size:clamp(28px,3vw,38px);font-weight:800;color:var(--text);display:inline-flex;align-items:baseline;gap:8px;letter-spacing:-.02em}
.nx-feat-metric-label{font-size:14px;font-weight:400;color:var(--muted);letter-spacing:0}
.nx-feat-cta{display:inline-flex;align-items:center;gap:6px;font-family:inherit;font-size:13px;font-weight:600;color:var(--text);background:none;border:none;cursor:pointer;padding:0;transition:gap .15s}
.nx-feat-cta:hover{gap:10px}

/* ── PROBLEM STATEMENT ── */
.nx-problem-section{padding:60px 0}
.nx-problem-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.nx-problem-card{background:#f5f5f7;border:1px solid rgba(0,0,0,.08);border-radius:var(--r);padding:28px 24px;display:flex;flex-direction:column}
.nx-problem-stat{font-size:clamp(30px,3.2vw,40px);font-weight:800;color:var(--text);letter-spacing:-.02em;margin-bottom:14px}
.nx-problem-desc{font-size:14px;color:var(--muted);line-height:1.65;margin:0;flex:1}
.nx-problem-icon{display:flex;color:var(--faint);margin-top:20px}
.nx-problem-source{text-align:center;font-size:12.5px;color:var(--muted);margin-top:28px}

/* ── LEAD CAPTURE + DIGITAL BUSINESS CARD ── */
.nx-lc-section{padding:20px 0 60px}
.nx-lc-photo{border-radius:var(--r);overflow:hidden;margin-bottom:20px;box-shadow:0 20px 50px rgba(0,0,0,.06)}
.nx-lc-photo img{width:100%;max-height:380px;object-fit:cover;display:block}
.nx-lc-photo--bottom{border-radius:0}
.nx-lc-photo--bottom img{max-height:260px}
.nx-lc-card{background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:var(--r);overflow:hidden;box-shadow:0 20px 50px rgba(0,0,0,.06)}
.nx-lc-tabs{display:grid;grid-template-columns:repeat(2,1fr)}
.nx-lc-tab{padding:20px 24px;text-align:center;font-size:16px;font-weight:600;color:var(--muted);background:#f5f5f7;border:none;border-bottom:1px solid rgba(0,0,0,.08);cursor:pointer;transition:background .2s,color .2s;font-family:inherit}
.nx-lc-tab:first-child{border-right:1px solid rgba(0,0,0,.08)}
.nx-lc-tab--active{background:#fff;color:var(--text);border-bottom-color:transparent;box-shadow:inset 0 -2px 0 var(--text)}
.nx-lc-grid{display:grid;grid-template-columns:repeat(3,1fr)}
.nx-lc-item{padding:40px 32px;text-align:center;border-right:1px solid rgba(0,0,0,.06)}
.nx-lc-item:last-child{border-right:none}
.nx-lc-icon{display:inline-flex;align-items:center;justify-content:center;width:60px;height:60px;border-radius:8px;background:#f5f5f7;color:var(--text);margin-bottom:20px}
.nx-lc-item-title{font-size:17px;font-weight:700;color:var(--text);margin:0 0 10px}
.nx-lc-item-desc{font-size:14px;color:var(--muted);line-height:1.6;max-width:260px;margin:0 auto}

/* ── HOW IT WORKS ── */
.nx-howit{padding:40px 0 20px}
.nx-howit-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:36px}
.nx-howit-num{display:block;font-size:13px;font-weight:500;color:#111;letter-spacing:.06em;margin-bottom:12px}
.nx-howit-title{font-size:19px;font-weight:400;color:var(--text);margin:0 0 8px;letter-spacing:-.01em}
.nx-howit-desc{font-size:14px;color:#000;line-height:1.65;margin:0}

/* ── SECURITY ── */

/* ── DARK CAPABILITIES ── */
.nx-caps-grid{display:flex;flex-wrap:wrap;gap:10px;padding-top:8px}
.nx-cap-tag{display:inline-flex;align-items:center;background:rgba(255,255,255,.06);color:rgba(255,255,255,.75);border:1px solid rgba(255,255,255,.1);border-radius:100px;padding:8px 16px;font-size:13px;font-weight:500;transition:all .2s;cursor:default}
.nx-cap-tag:hover{background:rgba(255,255,255,.12);color:#fff;border-color:rgba(255,255,255,.25)}

/* ── GALLERY HOVER CAROUSEL ── */
.nx-ghc-section{position:relative;overflow:hidden;padding:30px 0 50px;background:transparent;border-top:none;-webkit-mask-image:none;mask-image:none}
.nx-ghc-header{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:48px;gap:24px;flex-wrap:wrap}
.nx-ghc-header-left{max-width:520px}
.nx-ghc-h2{font-size:clamp(28px,3vw,44px);font-weight:300;line-height:1.1;letter-spacing:-.03em;color:#1d1d1f;margin-bottom:12px}
.nx-ghc-sub{font-size:15px;color:#6e6e73;line-height:1.6;max-width:440px}
.nx-gs-tray{background:#f5f5f7;border-radius:10px;padding:48px 48px 0;display:flex;gap:0;overflow:hidden;margin-top:40px}
.nx-gs-shot{flex:1;min-width:0;border-radius:6px 6px 0 0;overflow:hidden;box-shadow:0 20px 50px rgba(0,0,0,.12)}
.nx-gs-shot:first-child{flex:1.3}
.nx-gs-shot img{display:block;width:100%;height:640px;object-fit:cover;object-position:top left}
.nx-gs-shot:first-child img{mask-image:linear-gradient(to right,#000 92%,transparent 100%);-webkit-mask-image:linear-gradient(to right,#000 92%,transparent 100%)}
.nx-gs-shot:last-child img{mask-image:linear-gradient(to left,#000 95%,transparent 100%);-webkit-mask-image:linear-gradient(to left,#000 95%,transparent 100%)}
.nx-gs-caption{font-size:15px;color:#000;text-align:center;line-height:1.6;margin-top:24px}

/* ── SHOWCASE SECTIONS ── */
/* ── BENTO GRID (seguridad + integraciones + analytics + wallet) ── */
.nx-bento-section{padding:40px 0}
.nx-bento-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:8px}
.nx-bento-card{background:#f5f5f7;border:1px solid rgba(0,0,0,.08);overflow:hidden}
.nx-bento-a{grid-column:span 3;border-radius:20px 0 0 0}
.nx-bento-b{grid-column:span 2;border-radius:0 20px 0 0;display:flex;flex-direction:column}
.nx-bento-c{grid-column:span 2;border-radius:0 0 0 20px;display:flex;flex-direction:column;justify-content:center}
.nx-bento-d{grid-column:span 3;border-radius:0 0 20px 0;display:flex;flex-direction:column}
.nx-bento-heading{font-size:15px;font-weight:600;color:#1d1d1f;margin:0 0 6px;letter-spacing:-.01em}
.nx-bento-sub{font-size:13px;color:#6e6e73;line-height:1.5;max-width:340px;margin:0}
.nx-bento-center-heading{margin:28px auto 18px;max-width:280px;text-align:center;font-size:17px;font-weight:600;color:#1d1d1f;letter-spacing:-.01em;padding:0 20px}
/* A — seguridad */
.nx-bento-a-head{padding:26px 26px 0}
.nx-bento-a-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px 16px;padding:22px 26px 26px}
.nx-bento-a-icon{display:flex;color:#1d1d1f;margin-bottom:8px}
.nx-bento-a-icon svg{width:18px;height:18px}
.nx-bento-a-item-title{font-size:12.5px;font-weight:600;color:#1d1d1f;margin:0 0 3px}
.nx-bento-a-item-desc{font-size:11.5px;color:#6e6e73;line-height:1.5;margin:0}
/* B — analytics */
.nx-bento-b-imgwrap{padding:0 16px 16px;margin-top:auto}
.nx-bento-b-img{width:100%;display:block;border-radius:8px;border:1px solid rgba(0,0,0,.08)}
/* C — wallet */
.nx-bento-c-imgwrap{display:flex;justify-content:center;padding:0 12px 20px}
.nx-bento-c-img{width:100%;max-width:260px;height:auto;display:block;border-radius:10px;border:1px solid rgba(0,0,0,.08);box-shadow:0 8px 24px rgba(0,0,0,.08)}
/* D — integraciones */
.nx-bento-d-head{padding:26px 26px 0}
.nx-bento-d-grid{display:flex;align-items:center;justify-content:center;gap:28px;padding:22px 26px 26px;flex:1}
.nx-bento-d-box{width:124px;height:124px;flex-shrink:0;border-radius:22px;display:flex;align-items:center;justify-content:center}
.nx-bento-d-box--logo{background:#fafafa;border:1px solid rgba(0,0,0,.08)}
.nx-bento-d-box--logo svg{width:56px;height:56px}
.nx-bento-d-salesforce{width:64px;height:auto;object-fit:contain}
.nx-fan-section{padding:56px 0;background:transparent;position:relative;overflow:visible}
.nx-fan3-grid{display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center}
.nx-fan3-row{display:flex;justify-content:center;align-items:flex-end;padding:20px 0}
.nx-fan3-card{position:relative;width:195px;height:364px;border-radius:22px;overflow:hidden;background-size:cover;background-position:center;box-shadow:0 16px 52px rgba(0,0,0,.18);flex-shrink:0;margin:0 -20px}
.nx-fan3-card:nth-child(1){transform:rotate(-11deg) translateY(14px);z-index:1}
.nx-fan3-card:nth-child(2){transform:rotate(0deg);z-index:3}
.nx-fan3-card:nth-child(3){transform:rotate(11deg) translateY(14px);z-index:2}
.nx-fan3-overlay{position:absolute;inset:0;background:linear-gradient(to bottom,rgba(0,0,0,.10) 0%,rgba(0,0,0,.38) 38%,rgba(0,0,0,.72) 100%)}
.nx-fan3-content{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;padding:21px 12px 13px;height:100%;box-sizing:border-box}
.nx-fan3-av-wrap{position:relative;margin-bottom:8px}
.nx-fan3-av{width:57px;height:57px;border-radius:50%;object-fit:cover;border:3px solid rgba(255,255,255,.9);display:block;box-shadow:0 2px 12px rgba(0,0,0,.3)}
.nx-fan3-nfc{position:absolute;bottom:1px;right:1px;width:13px;height:13px;border-radius:50%;background:#2563EB;border:2px solid #fff}
.nx-fan3-name{font-size:14px;font-weight:700;color:#fff;text-align:center;margin-bottom:2px;letter-spacing:-.01em;text-shadow:0 1px 4px rgba(0,0,0,.4)}
.nx-fan3-role{font-size:10px;color:rgba(255,255,255,.75);text-align:center;margin-bottom:2px}
.nx-fan3-co{font-size:10px;color:rgba(255,255,255,.55);text-align:center;margin-bottom:10px}
.nx-fan3-save{width:100%;height:31px;border-radius:9px;background:rgba(255,255,255,.92);color:#111;border:none;font-size:10px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:5px;margin-bottom:6px;font-family:inherit}
.nx-fan3-link{width:100%;height:27px;border-radius:7px;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.12);display:flex;align-items:center;padding:0 9px;gap:6px;font-size:10px;color:rgba(255,255,255,.88);font-weight:500;margin-bottom:5px}
.nx-fan3-link svg{width:11px;height:11px;flex-shrink:0;color:rgba(255,255,255,.7)}
/* ── GLOBE CTA ── */
.nx-globe-section{padding:24px 0}
.nx-globe-card{position:relative;background:#f5f5f7;border:1px solid rgba(0,0,0,.08);border-radius:10px;overflow:hidden;padding:56px 48px;display:flex;align-items:center;justify-content:space-between;gap:40px;min-height:280px}
.nx-globe-text{position:relative;z-index:2;max-width:520px;text-align:left}
.nx-globe-h2{font-family:'abcNormal','Inter','Arial',sans-serif;font-size:clamp(21px,2.3vw,29px);font-weight:400;line-height:1.45;letter-spacing:-.01em;margin:0 0 26px}
.nx-globe-h2-dark{color:#1d1d1f}
.nx-globe-h2-light{color:#86868b}
.nx-globe-visual{position:absolute;bottom:-90px;right:-140px;width:520px;height:520px;z-index:1;pointer-events:none}
.nx-globe-canvas{width:100%;height:100%;display:block;cursor:grab}
.nx-showcase-h2{font-size:clamp(26px,3vw,38px);font-weight:800;line-height:1.15;letter-spacing:-.03em;color:var(--text);margin-bottom:14px}
.nx-showcase-body{font-size:16px;color:#000;line-height:1.7;margin-bottom:20px}
.nx-li-check{color:#22C55E;flex-shrink:0;margin-top:2px}
.nx-showcase-visual{border-radius:6px;overflow:hidden;box-shadow:0 12px 72px rgba(0,0,0,.16),0 0 0 1px rgba(0,0,0,.05),0 0 120px rgba(59,130,246,.07)}

/* ── API / INTEGRATIONS ── */
.nx-api-section{padding:40px 0 40px;background:transparent}
.nx-api-grid{display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center}
.nx-api-eyebrow{display:block;font-size:12px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:14px}
.nx-api-term{background:#ffffff}
.nx-api-term-bar{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;padding:12px 16px;background:linear-gradient(180deg,#fafafa,#f0f0f0);border-bottom:1px solid rgba(0,0,0,.06)}
.nx-api-term-tab{justify-self:center;font-size:11px;font-weight:600;color:#6e6e73;font-family:'SF Mono',ui-monospace,'Cascadia Code',monospace;letter-spacing:.02em}
.nx-api-term-copy{justify-self:end;color:#8e8e93;display:flex;align-items:center;cursor:pointer;transition:color .15s}
.nx-api-term-copy:hover{color:#1d1d1f}
.nx-api-term-body{padding:18px 0;font-family:'SF Mono',ui-monospace,'Cascadia Code',monospace;font-size:12.5px;line-height:1.75}
.nx-api-term-line{display:flex}
.nx-api-term-linenum{width:34px;flex-shrink:0;text-align:right;padding-right:16px;color:#b0b0b6;user-select:none}
.nx-api-term-code{flex:1;color:#1F2328;white-space:pre-wrap;overflow-wrap:anywhere;padding-right:18px}
.nx-api-term-cursor{display:inline-block;width:7px;height:14px;margin-left:2px;vertical-align:-2px;background:#1d1d1f;animation:nx-term-blink 1.1s step-end infinite}
@keyframes nx-term-blink{0%,49%{opacity:1}50%,100%{opacity:0}}
.nx-api-grid .nx-showcase-visual{border-radius:8px;box-shadow:0 20px 50px rgba(0,0,0,.10),0 0 0 1px rgba(0,0,0,.08);transition:none}
.nx-api-grid .nx-showcase-visual:hover{box-shadow:0 20px 50px rgba(0,0,0,.10),0 0 0 1px rgba(0,0,0,.08);transform:none}
@media (max-width: 768px) {
  .nx-api-section{padding:28px 0 28px}
  .nx-api-grid{grid-template-columns:1fr;gap:32px}
  .nx-api-grid .nx-showcase-visual{order:2}
  .nx-api-grid > div:not(.nx-showcase-visual){order:1}
  .nx-api-term-bar{padding:9px 12px}
  .nx-api-term-tab{font-size:10px}
  .nx-api-term-body{padding:14px 0;font-size:9px;line-height:1.6}
  .nx-api-term-linenum{width:22px;padding-right:10px}
  .nx-api-term-code{padding-right:14px}
  .nx-bento-section{padding:20px 0}
  .nx-bento-grid{grid-template-columns:1fr 1fr;gap:6px}
  .nx-bento-a{grid-column:span 1;border-radius:14px 0 0 0}
  .nx-bento-b{grid-column:span 1;border-radius:0 14px 0 0}
  .nx-bento-c{grid-column:span 1;border-radius:0 0 0 14px;justify-content:flex-start}
  .nx-bento-d{grid-column:span 1;border-radius:0 0 14px 0}
  .nx-bento-heading{font-size:11.5px;margin-bottom:3px}
  .nx-bento-sub{font-size:9.5px;line-height:1.4}
  .nx-bento-center-heading{font-size:11.5px;margin:14px auto 10px;padding:0 10px;max-width:150px}
  .nx-bento-a-head{padding:12px 12px 14px}
  .nx-bento-a-grid{display:none}
  .nx-bento-b-imgwrap{padding:0 10px 10px}
  .nx-bento-c-imgwrap{padding:0 6px 10px}
  .nx-bento-c-img{max-width:155px}
  .nx-bento-d-head{padding:12px 12px 0}
  .nx-bento-d-grid{gap:10px;padding:10px 12px 16px}
  .nx-bento-d-box{width:64px;height:64px;border-radius:14px}
  .nx-bento-d-box--logo svg{width:30px;height:30px}
  .nx-bento-d-salesforce{width:34px}
  .nx-fan-section{padding:32px 0}
  .nx-fan3-grid{grid-template-columns:1fr;gap:28px}
  .nx-fan3-row{order:1}
  .nx-fan3-grid>div:not(.nx-fan3-row){order:2;text-align:center}
  .nx-fan3-card{width:125px;height:234px;border-radius:16px;margin:0 -13px}
  .nx-fan3-card:nth-child(1){transform:rotate(-11deg) translateY(9px)}
  .nx-fan3-card:nth-child(3){transform:rotate(11deg) translateY(9px)}
  .nx-fan3-av{width:32px;height:32px;border-width:2px}
  .nx-fan3-name{font-size:9px}
  .nx-fan3-role,.nx-fan3-co{font-size:7px}
  .nx-fan3-save,.nx-fan3-link{height:19px;font-size:7px}
  .nx-globe-card{align-items:center;padding:22px 18px;border-radius:8px;min-height:0;gap:10px}
  .nx-globe-text{max-width:58%;text-align:left}
  .nx-globe-h2{font-size:13px;line-height:1.4;letter-spacing:0;margin-bottom:12px}
  .nx-globe-text .nx-btn-pill{height:34px;padding:0 14px;font-size:11.5px;gap:5px}
  .nx-globe-text .nx-btn-outline{height:34px;padding:0 14px;font-size:11.5px;gap:5px}
  .nx-globe-text > div{flex-direction:column;align-items:flex-start}
  .nx-globe-visual{width:150px;height:150px;bottom:-36px;right:-42px}
}

.nx-integrations-section{padding:40px 0 40px;background:transparent}
.nx-integrations-grid{display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center}
.nx-orbit-visual{border-radius:0;overflow:visible;box-shadow:none;background:transparent;display:flex;align-items:center;justify-content:center}
.nx-showcase-visual.nx-orbit-visual:hover{transform:none;box-shadow:none}
.nx-orbit-wrap{position:relative;width:100%;max-width:480px;aspect-ratio:1/1;margin:0 auto}
.nx-orbit-core{position:absolute;top:50%;left:50%;width:18%;aspect-ratio:1/1;border-radius:50%;transform:translate(-50%,-50%);background:radial-gradient(circle at 32% 28%,#3d3d40,#101012 55%,#000 100%);box-shadow:0 0 60px rgba(0,0,0,.3),inset 0 0 0 1px rgba(255,255,255,.1);z-index:5;display:flex;align-items:center;justify-content:center}
.nx-orbit-core::before{content:'';position:absolute;inset:-45%;border-radius:50%;background:radial-gradient(circle,rgba(0,0,0,.35),transparent 62%);animation:nxOrbitPulse 3.2s ease-in-out infinite;z-index:-1}
.nx-orbit-core-mark{position:relative;width:56%;height:56%;object-fit:contain;filter:brightness(0) invert(1);opacity:.95}
@keyframes nxOrbitPulse{0%,100%{opacity:.3;transform:scale(.85)}50%{opacity:.7;transform:scale(1.18)}}
.nx-orbit-ring,.nx-orbit-spoke{position:absolute;top:50%;left:50%;border-radius:50%}
.nx-orbit-r1{width:36%;height:36%;margin:-18% 0 0 -18%}
.nx-orbit-r2{width:66%;height:66%;margin:-33% 0 0 -33%}
.nx-orbit-r3{width:98%;height:98%;margin:-49% 0 0 -49%}
.nx-orbit-ring{border:1px dashed rgba(0,0,0,.09);pointer-events:none}
.nx-orbit-bubble{position:absolute;top:0;left:50%;width:52px;height:52px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 18px rgba(0,0,0,.12),0 0 0 1px rgba(0,0,0,.05)}
@keyframes nxOrbitCw{from{transform:rotate(var(--start-angle))}to{transform:rotate(calc(var(--start-angle) + 360deg))}}
@keyframes nxOrbitCcw{from{transform:rotate(var(--start-angle))}to{transform:rotate(calc(var(--start-angle) - 360deg))}}
@keyframes nxOrbitCounterCw{from{transform:translate(-50%,-50%) rotate(calc(-1 * var(--start-angle)))}to{transform:translate(-50%,-50%) rotate(calc(-1 * var(--start-angle) - 360deg))}}
@keyframes nxOrbitCounterCcw{from{transform:translate(-50%,-50%) rotate(calc(-1 * var(--start-angle)))}to{transform:translate(-50%,-50%) rotate(calc(-1 * var(--start-angle) + 360deg))}}
@media (max-width: 768px) {
  .nx-integrations-section{padding:28px 0 28px}
  .nx-integrations-grid{grid-template-columns:1fr;gap:32px}
  .nx-orbit-wrap{max-width:260px}
  .nx-orbit-bubble{width:38px;height:38px}
  .nx-orbit-bubble svg{width:18px;height:18px}
}

/* ── DASHBOARD MOCKUP ── */
.nx-am-wrap{display:flex;background:#fff;border:1px solid rgba(0,0,0,.06);border-radius:16px;overflow:hidden;font-family:'Inter',system-ui,sans-serif;box-shadow:0 24px 72px rgba(0,0,0,.35),0 0 0 1px rgba(255,255,255,.04)}
.nx-am-sidebar{width:150px;flex-shrink:0;background:#fff;border-right:1px solid rgba(0,0,0,.08);padding:16px 9px}
.nx-am-side-card{margin:0 4px 14px;padding:8px 9px;border:1px solid rgba(0,0,0,.07);border-radius:10px;background:#f9f9fa}
.nx-am-side-name{font-size:10px;font-weight:600;color:#1d1d1f;margin-bottom:4px}
.nx-am-side-badge{display:inline-flex;font-size:8px;font-weight:600;padding:2px 6px;border-radius:980px;background:#fff;color:#0A0A0A;border:1px solid #0A0A0A}
.nx-am-side-group{margin-top:12px}
.nx-am-side-group:first-of-type{margin-top:0}
.nx-am-side-label{font-size:8px;font-weight:700;color:#a1a1a6;text-transform:uppercase;letter-spacing:.05em;padding:0 7px 4px}
.nx-am-side-item{padding:6px 7px;border-radius:8px;color:#48484a;font-size:10px;font-weight:500;margin-bottom:1px}
.nx-am-side-item--on{background:#0A0A0A;color:#fff;font-weight:600}
.nx-am-main{flex:1;padding:20px 22px;overflow:hidden;background:#f5f5f7;display:flex;flex-direction:column;gap:14px;min-width:0}
.nx-am-head{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px}
.nx-am-h1{font-size:18px;font-weight:300;letter-spacing:-.02em;color:#1d1d1f;margin:0;font-family:'abcNormal','Inter','Arial',sans-serif}
.nx-am-h1-sub{font-size:11px;color:#6e6e73;margin:4px 0 0}
.nx-am-slug{color:#0071e3;font-family:ui-monospace,monospace}
.nx-am-seg{display:flex;background:rgba(0,0,0,0.055);border-radius:9px;padding:2px;gap:1px}
.nx-am-seg-btn{border:none;border-radius:7px;padding:4px 9px;font-size:10px;font-weight:500;cursor:pointer;font-family:-apple-system,BlinkMacSystemFont,sans-serif;transition:background .15s,box-shadow .15s,color .15s;background:transparent;color:#6e6e73}
.nx-am-seg-btn--on{background:#0A0A0A;color:#fff;box-shadow:none}
.nx-am-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
.nx-am-kpi{background:#fff;border-radius:12px;border:1px solid rgba(0,0,0,0.07);border-top:3px solid;padding:12px}
.nx-am-kpi-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:6px}
.nx-am-kpi-icon{width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.nx-am-kpi-tag{font-size:7px;font-weight:600;padding:2px 6px;border-radius:6px;text-transform:uppercase;letter-spacing:.02em;white-space:nowrap}
.nx-am-kpi-num{font-size:18px;font-weight:700;color:#1d1d1f;letter-spacing:-.03em;line-height:1;margin-bottom:3px;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif}
.nx-am-kpi-lbl{font-size:10px;color:#1d1d1f;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.nx-am-charts{display:flex;gap:10px;flex:1;min-height:0}
.nx-am-linechart-card{flex:1;background:#fff;border-radius:12px;border:1px solid rgba(0,0,0,0.07);padding:14px;display:flex;flex-direction:column;min-width:0}
.nx-am-card-title{font-size:11px;font-weight:600;color:#1d1d1f;letter-spacing:-.01em;margin-bottom:8px}
.nx-am-linechart{height:112px}
.nx-am-legend{display:flex;flex-wrap:wrap;gap:8px 14px;margin-top:10px;padding:8px 10px;background:rgba(0,0,0,0.025);border-radius:8px;border:1px solid rgba(0,0,0,0.05)}
.nx-am-legend-item{display:flex;align-items:center;gap:5px;font-size:9px;font-weight:500;color:#48484a}
.nx-am-legend-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0}
.nx-am-legend-dash{width:11px;height:2px;border-radius:99px;flex-shrink:0}
.nx-am-barrow{display:flex;align-items:center;gap:8px;padding:5px 0}
.nx-am-barrow-rank{font-size:9px;font-weight:700;color:#aeaeb2;width:11px;flex-shrink:0}
.nx-am-barrow-icon{width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.nx-am-barrow-mid{flex:1;min-width:0}
.nx-am-barrow-name{font-size:10px;font-weight:500;color:#1d1d1f;margin-bottom:3px}
.nx-am-barrow-track{height:4px;border-radius:99px;background:rgba(0,0,0,.06);overflow:hidden}
.nx-am-barrow-fill{height:100%;border-radius:99px}
.nx-am-barrow-val{font-size:10px;font-weight:700;color:#1d1d1f;flex-shrink:0}
.nx-lm-wrap{display:flex;background:#fff;border:1px solid rgba(0,0,0,.06);border-radius:16px;overflow:hidden;font-family:'Inter',system-ui,sans-serif;box-shadow:0 24px 72px rgba(0,0,0,.35),0 0 0 1px rgba(255,255,255,.04)}
.nx-lm-sidebar{width:160px;flex-shrink:0;background:#fff;border-right:1px solid rgba(0,0,0,.08);padding:18px 10px}
.nx-lm-side-card{margin:0 4px 16px;padding:9px 10px;border:1px solid rgba(0,0,0,.07);border-radius:10px;background:#f9f9fa}
.nx-lm-side-name{font-size:11px;font-weight:600;color:#1d1d1f;margin-bottom:5px}
.nx-lm-side-badge{display:inline-flex;font-size:9px;font-weight:600;padding:2px 7px;border-radius:980px;background:#fff;color:#0A0A0A;border:1px solid #0A0A0A}
.nx-lm-side-group{margin-top:14px}
.nx-lm-side-group:first-of-type{margin-top:0}
.nx-lm-side-label{font-size:9px;font-weight:700;color:#a1a1a6;text-transform:uppercase;letter-spacing:.05em;padding:0 8px 5px}
.nx-lm-side-item{padding:7px 8px;border-radius:8px;color:#48484a;font-size:11px;font-weight:500;margin-bottom:1px}
.nx-lm-side-item--on{background:#0A0A0A;color:#fff;font-weight:600}
.nx-lm-main{flex:1;padding:22px 24px;background:#f5f5f7;min-width:0}
.nx-lm-head{margin-bottom:16px}
.nx-lm-h1{font-size:20px;font-weight:300;letter-spacing:-.02em;color:#1d1d1f;margin:0;font-family:'abcNormal','Inter','Arial',sans-serif}
.nx-lm-h1-sub{font-size:12px;color:#6e6e73;margin:3px 0 0}
.nx-lm-name{font-size:13px;font-weight:600;color:#1d1d1f;margin:0 0 6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.nx-lm-badge{display:inline-flex;font-size:9px;font-weight:600;padding:2px 8px;border-radius:980px;margin-bottom:8px}
.nx-lm-badge--new{background:rgba(22,163,74,.1);color:#16A34A}
.nx-lm-badge--cont{background:rgba(220,38,38,.1);color:#DC2626}
.nx-lm-row{display:flex;align-items:center;gap:5px;font-size:10px;color:#6e6e73;margin-top:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nx-lm-row svg{flex-shrink:0}
.nx-lm-actions{display:flex;gap:6px;margin-top:13px}
.nx-lm-btn-p{flex:1;height:26px;background:#fff;color:#0A0A0A;border:1px solid rgba(0,0,0,.1);border-radius:8px;font-size:10px;font-weight:500;display:flex;align-items:center;justify-content:center;gap:4px}
.nx-lm-metrics{display:flex;background:#fff;border-radius:12px;border:1px solid rgba(0,0,0,.08);margin-bottom:10px}
.nx-lm-metric{flex:1;padding:8px 10px;border-right:1px solid rgba(0,0,0,.08)}
.nx-lm-metric:last-child{border-right:none}
.nx-lm-metric-val{font-size:14px;font-weight:700;color:#1d1d1f;letter-spacing:-.02em}
.nx-lm-metric-lbl{font-size:8px;color:#6e6e73;font-weight:500;margin-top:1px}
.nx-lm-kanban{display:flex;gap:10px}
.nx-lm-col{flex:1;background:#f5f5f7;border-radius:14px;border:1px solid rgba(0,0,0,.08);overflow:hidden;min-width:0}
.nx-lm-col-head{display:flex;align-items:center;justify-content:space-between;padding:9px 11px;background:#fff;border-top:3px solid;border-bottom:1px solid rgba(0,0,0,.08);font-size:11px;font-weight:600;color:#1d1d1f}
.nx-lm-col-count{font-size:9px;font-weight:600;background:rgba(0,0,0,.06);color:#6e6e73;padding:1px 7px;border-radius:980px}
.nx-lm-col-body{padding:8px;display:flex;flex-direction:column;gap:8px}
.nx-lm-pcard{background:#fff;border-radius:11px;border:1px solid rgba(0,0,0,.08);border-left:3px solid;padding:9px 10px}
.nx-lm-pcard-top{display:flex;align-items:center;justify-content:space-between;gap:6px;margin-bottom:5px}
.nx-lm-pname{font-size:11px;font-weight:600;color:#1d1d1f;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;align-items:center;gap:4px}
.nx-lm-urgency{font-size:8px;font-weight:600;padding:2px 7px;border-radius:980px;white-space:nowrap;flex-shrink:0}
.nx-lm-pcontact{font-size:9px;color:#6e6e73;display:flex;align-items:center;gap:4px;margin-top:3px}
.nx-lm-pactions{display:flex;gap:5px;margin-top:8px}
.nx-lm-btn-contact{flex:1;height:22px;border-radius:7px;font-size:9px;font-weight:500;display:flex;align-items:center;justify-content:center;gap:3px;background:#fff;color:#0A0A0A;border:1px solid rgba(0,0,0,.1)}
.nx-ap2-wrap{display:flex;gap:14px;flex:1;min-height:0}
.nx-ap2-left{flex:1;display:flex;flex-direction:column;gap:10px;min-width:0}
.nx-ap2-card{background:#fff;border-radius:12px;border:1px solid rgba(0,0,0,.07);padding:12px}
.nx-ap2-card-title{font-size:11px;font-weight:600;color:#1d1d1f;margin-bottom:8px}
.nx-ap2-tpl{display:flex;gap:6px}
.nx-ap2-tpl-btn{flex:1;padding:7px;border-radius:9px;border:1.5px solid rgba(0,0,0,.1);font-size:10px;font-weight:600;text-align:center;color:#6e6e73}
.nx-ap2-tpl-btn--on{border-color:#0A0A0A;background:#0A0A0A;color:#fff}
.nx-ap2-themes{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.nx-ap2-theme{border-radius:10px;border:1.5px solid rgba(0,0,0,.1);padding:8px 4px;text-align:center}
.nx-ap2-theme--on{border-color:#0A0A0A;box-shadow:0 0 0 1px #0A0A0A}
.nx-ap2-theme-dot{width:16px;height:16px;border-radius:50%;margin:0 auto 4px}
.nx-ap2-theme-lbl{font-size:8px;color:#48484a;font-weight:500}
.nx-ap2-swatchrow{display:flex;align-items:center;gap:8px;margin-top:8px}
.nx-ap2-swatch{width:22px;height:22px;border-radius:7px;border:1px solid rgba(0,0,0,.12);flex-shrink:0}
.nx-ap2-swatch-hex{font-size:9px;color:#6e6e73;font-family:ui-monospace,monospace}
.nx-ap2-custom-field{margin-bottom:9px}
.nx-ap2-custom-lbl{display:block;font-size:9px;font-weight:600;color:#48484a;margin-bottom:5px}
.nx-ap2-custom-row{display:flex;align-items:center;gap:7px}
.nx-ap2-mini-swatch{width:22px;height:22px;border-radius:6px;flex-shrink:0}
.nx-ap2-custom-hex{font-size:9.5px;color:#1d1d1f;font-family:ui-monospace,monospace;background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:6px;padding:3px 8px;flex:1}
.nx-ap2-palette-box{background:#FAFAFA;border:1px solid rgba(0,0,0,.08);border-radius:8px;padding:8px;margin-bottom:9px}
.nx-ap2-palette-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
.nx-ap2-palette-head>span:first-child{font-size:9px;font-weight:600;color:#374151}
.nx-ap2-apply-btn{font-size:8px;font-weight:600;background:#0A0A0A;color:#fff;padding:3px 8px;border-radius:6px}
.nx-ap2-palette-swatches{display:grid;grid-template-columns:repeat(4,1fr);gap:5px}
.nx-ap2-palette-item{text-align:center}
.nx-ap2-palette-sw{width:100%;height:18px;border-radius:5px;border:1px solid rgba(0,0,0,.08);margin-bottom:3px}
.nx-ap2-palette-item span{font-size:7px;color:#6B7280}
.nx-ap2-save-btn{width:100%;background:#0A0A0A;color:#fff;border:none;border-radius:8px;padding:8px;font-size:10px;font-weight:600}
.nx-ap2-phone-wrap{width:126px;flex-shrink:0;display:flex;flex-direction:column;align-items:center}
.nx-ap2-phone-lbl{font-size:9px;color:#6e6e73;margin-bottom:6px;font-weight:500}
.nx-ap2-phone{width:126px;height:230px;border-radius:20px;overflow:hidden;border:1px solid rgba(0,0,0,.1);box-shadow:0 8px 24px rgba(0,0,0,.12);display:flex;flex-direction:column;align-items:center;padding:18px 10px}
.nx-tm-badge{display:inline-flex;align-items:center;gap:4px;font-size:9px;font-weight:600;padding:4px 9px;border-radius:9px}
.nx-tm-card{background:#fff;border-radius:16px;border:1px solid rgba(0,0,0,0.07);box-shadow:0 1px 3px rgba(0,0,0,.05)}
.nx-tm-search{display:flex;align-items:center;gap:5px;background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:8px;padding:4px 9px;font-size:9px;color:#aeaeb2;flex-shrink:0}
.nx-tm-mgrid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}
.nx-tm-mcard{background:#fff;border-radius:13px;border:1px solid rgba(0,0,0,.07);overflow:hidden}
.nx-tm-mcard-body{padding:9px 9px 0}
.nx-tm-mcard-top{display:flex;align-items:flex-start;gap:7px;margin-bottom:7px}
.nx-tm-avatar{width:30px;height:30px;border-radius:9px;overflow:hidden;flex-shrink:0}
.nx-tm-avatar img{width:100%;height:100%;object-fit:cover;display:block}
.nx-tm-mcard-expand{width:100%;border:none;background:transparent;border-top:1px solid rgba(0,0,0,.06);padding:5px 0;display:flex;align-items:center;justify-content:center;margin-top:8px}

/* ── HUB filters row (mockup) ── */
.nx-hb-filters{display:flex;gap:8px;margin-top:10px}
.nx-hb-search{flex:1;display:flex;align-items:center;gap:6px;height:28px;padding:0 10px;border:1px solid rgba(0,0,0,.1);border-radius:8px;background:#fff;font-size:10px;color:#aeaeb2}
.nx-hb-select{display:flex;align-items:center;gap:5px;height:28px;padding:0 10px;border:1px solid rgba(0,0,0,.1);border-radius:8px;background:#fff;font-size:10px;color:#48484a;white-space:nowrap}

/* ── PERFILES table (mockup) ── */
.nx-pm-table-card{background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:14px;overflow:hidden}
.nx-pm-row{display:grid;grid-template-columns:2fr 0.8fr 0.9fr 1fr;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid rgba(0,0,0,.05)}
.nx-pm-row:last-child{border-bottom:none}
.nx-pm-row--head{background:#FAFAFA;padding:8px 14px}
.nx-pm-row--head span{font-size:8px;font-weight:700;color:#9a9a9a;text-transform:uppercase;letter-spacing:.05em}
.nx-pm-profile-cell{display:flex;align-items:center;gap:8px}
.nx-pm-avatar{width:26px;height:26px;border-radius:50%;object-fit:cover;flex-shrink:0}
.nx-pm-name{font-size:10.5px;font-weight:600;color:#1d1d1f}
.nx-pm-url{font-size:8.5px;color:#9a9a9a;font-family:ui-monospace,monospace;margin-top:1px}
.nx-pm-visits{font-size:10px;font-weight:600;color:#1d1d1f}
.nx-pm-actions{display:flex;gap:4px;justify-content:flex-end}
.nx-pm-icon-btn{width:20px;height:20px;border-radius:6px;border:1px solid rgba(0,0,0,.08);background:#fff;display:flex;align-items:center;justify-content:center;color:#525252}

/* ── PHONE MOCKUP ── */
@keyframes nxPhoneFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes nxPhoneGlow{0%,100%{box-shadow:0 20px 60px rgba(0,0,0,.18),0 0 0 1px rgba(255,255,255,.1) inset,0 0 60px rgba(37,99,235,.06)}50%{box-shadow:0 32px 80px rgba(0,0,0,.26),0 0 0 1px rgba(255,255,255,.14) inset,0 0 100px rgba(37,99,235,.18)}}

/* ── PHONE DUO (perfil público + edición lado a lado) ── */
@media(min-width:641px){
  .nx-phone-duo-cell:first-child{transform:rotateY(-22deg) rotateX(5deg)}
  .nx-phone-duo-cell:last-child{transform:rotateY(22deg) rotateX(5deg)}
  .nx-phone-duo:hover .nx-phone-duo-cell:first-child{transform:rotateY(-8deg) rotateX(2deg)}
  .nx-phone-duo:hover .nx-phone-duo-cell:last-child{transform:rotateY(8deg) rotateX(2deg)}
}

/* ── PHONE MOCKUP — EDICIÓN ── */

/* ── HOW IT WORKS ── */

/* ── PLANS ── */
.nx-plans{padding:100px 0;background:transparent}
.nx-plans-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:28px;max-width:1180px;margin:0 auto}
.nx-plan{background:#fff;border:1px solid rgba(0,0,0,.05);border-radius:8px;padding:36px 32px;position:relative;box-shadow:0 2px 16px rgba(0,0,0,.06);transition:box-shadow .2s}
.nx-plan:hover{box-shadow:0 4px 28px rgba(0,0,0,.1)}
.nx-plan--featured{overflow:hidden;border:none;box-shadow:0 8px 32px rgba(0,0,0,.18)}
@keyframes nx-beam-rotate{to{transform:translate(-50%,-50%) rotate(360deg)}}
.nx-plan--featured::before{content:'';position:absolute;top:50%;left:50%;width:260%;height:260%;transform:translate(-50%,-50%) rotate(0deg);background:conic-gradient(from 0deg,transparent 0%,#8a8a8a 8%,#000 18%,transparent 28%);animation:nx-beam-rotate 3s linear infinite;z-index:0;pointer-events:none}
.nx-plan--featured::after{content:'';position:absolute;inset:2px;border-radius:6px;background:#fff;z-index:1;pointer-events:none}
.nx-plan--featured>*{position:relative;z-index:2}
.nx-plan-badge{display:inline-block;background:rgba(0,0,0,.08);color:#000;border-radius:980px;padding:4px 14px;font-size:11px;font-weight:600;margin-bottom:20px;letter-spacing:.01em}
.nx-plan-name{font-size:22px;font-weight:400;color:#1d1d1f;letter-spacing:-.02em;margin-bottom:6px;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif}
.nx-plan-desc{font-size:14px;color:#6e6e73;line-height:1.6;margin-bottom:24px}
.nx-plan-list{list-style:none;display:flex;flex-direction:column;gap:12px;margin-bottom:32px}
.nx-plan-li{font-size:14px;color:#1d1d1f;display:flex;align-items:flex-start;gap:10px}
.nx-li-check{color:#000;flex-shrink:0;margin-top:1px}
.nx-plan-btn{width:100%;justify-content:center;height:48px;border-radius:8px;font-size:15px}

/* ── PLAN PRICING ── */
.nx-plan-price-wrap{margin:8px 0 24px;padding:20px 0;border-top:1px solid rgba(0,0,0,.04);border-bottom:1px solid rgba(0,0,0,.04)}
.nx-plan-price-old-row{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.nx-plan-price-old{font-size:13px;color:#b0b0b5;text-decoration:line-through}
.nx-plan-price-note{font-size:10px;font-weight:600;letter-spacing:.02em;color:#000;background:rgba(0,0,0,.08);border-radius:980px;padding:2px 9px}
.nx-plan-price-row{display:flex;align-items:baseline;gap:6px}
.nx-plan-price-main{font-size:52px;font-weight:700;line-height:1;letter-spacing:-.04em;color:#1d1d1f;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif}
.nx-plan-price-sub{font-size:13px;font-weight:400;color:#6e6e73}
.nx-plan-price-contact{min-height:64px;display:flex;flex-direction:column;justify-content:center}
.nx-plan-price-contact-main{font-size:26px;font-weight:600;line-height:1.2;letter-spacing:-.02em;color:#1d1d1f;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif}
.nx-plan-price-contact-sub{font-size:13px;font-weight:400;color:#6e6e73;margin-top:6px;line-height:1.4}

/* ── FEATURED PLAN OVERRIDES ── */
.nx-plan--featured .nx-btn-pill{background:#000;color:#fff}
.nx-plan--featured .nx-btn-pill:hover{background:#262626;opacity:1}

/* ── CTA CARD ── */
@keyframes nx-price-pop{0%{opacity:0;transform:translateY(6px)}100%{opacity:1;transform:translateY(0)}}
.nx-billing-toggle{display:inline-flex;align-items:center;gap:4px;background:rgba(0,0,0,0.05);border-radius:980px;padding:4px;margin-top:24px}
.nx-billing-opt{background:none;border:none;border-radius:980px;padding:8px 20px;font-size:14px;font-weight:500;color:var(--muted);cursor:pointer;transition:all .2s;font-family:inherit;display:flex;align-items:center;gap:8px;white-space:nowrap}
.nx-billing-opt--on{background:#fff;color:var(--text);box-shadow:0 1px 4px rgba(0,0,0,0.12)}
.nx-billing-save{font-size:11px;font-weight:600;background:#000;color:#fff;padding:2px 8px;border-radius:980px;letter-spacing:.01em}
.nx-plan-price-anim{animation:nx-price-pop .25s ease both}
.nx-plans-mobile-tabs{display:none}
.nx-faq{padding:60px 0}
.nx-faq-list{max-width:720px;margin:0 auto;display:flex;flex-direction:column;gap:0}
.nx-faq-item{border-bottom:1px solid var(--border);cursor:pointer;padding:14px 0;transition:background .15s}
.nx-faq-item:first-child{border-top:1px solid var(--border)}
.nx-faq-q{display:flex;align-items:center;justify-content:space-between;gap:16px}
.nx-faq-q>span{font-size:15px;font-weight:400;color:var(--text);letter-spacing:-.02em;line-height:1.4;font-family:'Inter',system-ui,sans-serif}
.nx-faq-icon{width:26px;height:26px;border-radius:50%;background:var(--border);display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .2s,transform .25s;color:var(--text)}
.nx-faq-item--open .nx-faq-icon{background:var(--text);color:#fff;transform:rotate(45deg)}
.nx-faq-plus{display:block}
.nx-faq-minus{display:none}
.nx-faq-item--open .nx-faq-plus{display:none}
.nx-faq-item--open .nx-faq-minus{display:block}
.nx-faq-a{margin:8px 0 2px;font-size:14px;color:var(--muted);line-height:1.6;letter-spacing:-.01em;font-family:'Inter',system-ui,sans-serif;max-width:90%}
/* ── FOOTER ── */
.nx-footer{background:transparent;padding:16px 0 0}
.nx-footer-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr 1fr;gap:14px;padding-bottom:14px;border-bottom:1px solid rgba(0,0,0,.06)}
.nx-footer-sec-btn{display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;margin-bottom:6px;font-family:inherit}
.nx-footer-sec-chevron{color:rgba(0,0,0,.4);transition:transform .2s ease;flex-shrink:0;margin-top:1px}
.nx-footer-sec-open{transform:rotate(180deg)}
.nx-footer-sec-text{font-size:11px;color:rgba(0,0,0,.5);line-height:1.4;margin:0}
.nx-footer-socials{display:flex;gap:8px;margin-top:6px}
.nx-footer-social{width:22px;height:22px;border-radius:6px;background:rgba(0,0,0,.04);border:1px solid rgba(0,0,0,.08);color:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;text-decoration:none;transition:all .15s}
.nx-footer-social:hover{border-color:#000;color:#000}
.nx-footer-col-title{font-size:10px;font-weight:400;color:#111;text-transform:uppercase;letter-spacing:.07em;margin-bottom:6px}
.nx-footer-links{display:flex;flex-direction:column;gap:5px}
.nx-footer-links a{font-size:12px;color:rgba(0,0,0,.5);text-decoration:none;transition:color .15s}
.nx-footer-links a:hover{color:#000}
.nx-footer-link-btn{background:none;border:none;cursor:pointer;font-family:inherit;font-size:12px;color:rgba(0,0,0,.5);text-align:left;padding:0;transition:color .15s}
.nx-footer-link-btn:hover{color:#000}
.nx-footer-detail{font-size:11px;color:rgba(0,0,0,.4)}
.nx-footer-bottom{padding:6px 0;display:flex;align-items:center;justify-content:space-between;gap:14px}
.nx-footer-bottom>span{font-size:11px;color:rgba(0,0,0,.4)}
.nx-currency-select{font-size:11px;color:rgba(0,0,0,.5);background:transparent;border:1px solid rgba(0,0,0,.12);border-radius:6px;padding:3px 6px;cursor:pointer;font-family:'Inter',system-ui,sans-serif;outline:none;transition:border-color .15s,color .15s}
.nx-currency-select:hover{border-color:rgba(0,0,0,.28);color:rgba(0,0,0,.75)}
.nx-currency-select:focus{border-color:rgba(0,0,0,.4)}


/* ── QUOTATION MODAL ── */
.qm-overlay{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.5);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn .2s ease}
.qm-modal{background:#fff;border-radius:16px;max-width:620px;width:100%;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.15);animation:fadeIn .25s cubic-bezier(.16,1,.3,1)}
.qm-head{display:flex;align-items:flex-start;justify-content:space-between;padding:24px 28px;border-bottom:1px solid var(--border)}
.qm-head h2{font-size:17px;font-weight:400;color:var(--text);margin-bottom:3px}
.qm-head p{font-size:13px;color:var(--muted)}
.qm-x{width:30px;height:30px;border-radius:8px;background:#F5F5F4;border:1px solid var(--border);cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--muted);flex-shrink:0;transition:background .15s}
.qm-x:hover{background:var(--border)}
.qm-body{padding:24px 28px;overflow-y:auto;flex:1}
.qm-field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.qm-field label{font-size:13px;font-weight:600;color:var(--text)}
.qm-req{color:#DC2626}
.qm-field input,.qm-field select,.qm-field textarea{border:1px solid var(--border);border-radius:8px;padding:10px 12px;font-size:14px;color:var(--text);background:#fff;outline:none;font-family:inherit;transition:border .15s,box-shadow .15s}
.qm-field input:focus,.qm-field select:focus,.qm-field textarea:focus{border-color:var(--text);box-shadow:0 0 0 3px rgba(17,17,17,.06)}
.qm-field textarea{resize:vertical;min-height:80px}
.qm-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.qm-qty-row{display:flex;align-items:center;gap:6px;border:1px solid var(--border);border-radius:8px;padding:4px 8px}
.qm-qty-btn{width:28px;height:28px;border-radius:6px;border:1px solid var(--border);background:#F5F5F4;color:var(--muted);font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center}
.qm-qty-input{flex:1;border:none;text-align:center;font-size:14px;font-weight:600;color:var(--text);outline:none}
.qm-qty-toggle{display:flex;gap:12px;margin-top:6px}
.qm-qty-toggle label{font-size:12px;color:var(--muted);cursor:pointer;display:flex;align-items:center;gap:4px}
.qm-error{font-size:12px;color:#DC2626;margin-top:3px}
.qm-submit{width:100%;height:48px;border-radius:100px;background:var(--text);color:#fff;font-size:15px;font-weight:700;border:none;cursor:pointer;margin-top:8px;font-family:inherit;transition:opacity .15s}
.qm-submit:hover{opacity:.85}
.qm-submit:disabled{opacity:.5;cursor:not-allowed}
.qm-success{text-align:center;padding:40px 20px;animation:fadeIn .3s ease}
.qm-success-ico{width:52px;height:52px;border-radius:50%;background:#F0FDF4;color:#16A34A;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;border:1px solid #BBF7D0}
.qm-success h3{font-size:17px;font-weight:400;color:var(--text);margin-bottom:8px}
.qm-success p{font-size:14px;color:var(--muted);line-height:1.6}

/* ── GHC ── */
.nx-ghc-conectar{color:#1d1d1f}
.nx-ghc-mesh{display:none}

/* ── RESPONSIVE ── */
@media(max-width:1024px){
  .nx-container{padding:0 28px}
  .nx-nav-inner{padding:0 28px}
  .nx-nav{left:12px;right:12px;top:12px}
  .nx-showcase-grid{grid-template-columns:1fr;gap:40px}
  .nx-fan-section{padding:20px 0 8px}
  .nx-dark-grid{grid-template-columns:1fr;gap:40px}
  .nx-ghc-section{padding:20px 0 32px}
  .nx-ghc-header{flex-direction:column;align-items:flex-start;margin-bottom:28px}
  .nx-ghc-h2{font-size:clamp(24px,5vw,32px)}
  .nx-gs-tray{padding:32px 32px 0}
  .nx-gs-shot img{height:460px}
  .nx-plans-grid{grid-template-columns:1fr;max-width:400px;margin:0 auto}
  .nx-footer-grid{grid-template-columns:1fr 1fr;gap:32px}
  .nx-nav-links{display:none}
  .nx-hamburger{display:flex}
  .nx-feat-grid{grid-template-columns:1fr}
  .nx-feat-card{padding:24px}
  .nx-howit-grid{grid-template-columns:1fr;gap:28px}
  .nx-security-grid{grid-template-columns:repeat(2,1fr);gap:28px}
  .nx-problem-grid{grid-template-columns:repeat(2,1fr);gap:20px}
}
@media(max-width:640px){
  /* ─ Layout ─ */
  .nx-container{padding:0 18px}
  .nx-nav-inner{padding:0 14px;height:46px}
  .nx-nav{left:8px;right:8px;top:8px;border-radius:15px}
  .nx-nav-left .h-8{height:24px}
  .nx-btn-ghost{display:none}
  .nx-btn-acceder{display:inline-flex;align-items:center;background:var(--text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 16px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
  .nx-nav-right .nx-btn-pill{display:none}
  .nx-nav-right .nx-lang-btn{display:none}
  .nx-account-name{display:none}
  .nx-account-trigger{padding-right:4px}
  .nx-account-menu{right:-4px}

  /* ─ Phone duo (siempre lado a lado, achicado) ─ */

  /* ─ Hero ─ */
  .nx-hero{padding:120px 0 0}
  .nx-hero-h1{font-size:clamp(42px,11vw,56px);line-height:1.08}
  .nx-hero-sub{font-size:16px;max-width:340px}
  .nx-hero-sub br{display:none}
  .nx-btn-pill--lg{height:52px;font-size:15px;padding:0 28px}

  /* ─ Clients carousel — franja compacta en mobile ─ */
  .nx-hero-clients{margin-top:20px;padding:12px 0 6px}
  .nx-customers-grid{display:flex;flex-wrap:wrap;justify-content:center;gap:18px 16px;max-width:100%}
  .nx-video-section{padding:20px 0 36px}
  .nx-customers-grid .nx-customers-logo{flex:0 0 calc((100% - 32px) / 3)}
  /* Orden propio en mobile (BICE, DXC, Vambe / UAI, Tasky, UTFSM / Regulatek, LiveUp, S4E),
     distinto al de desktop — mismo array, solo se reordena visualmente. */
  .nx-customers-grid .nx-customers-logo:nth-child(1){order:1}
  .nx-customers-grid .nx-customers-logo:nth-child(2){order:9}
  .nx-customers-grid .nx-customers-logo:nth-child(3){order:2}
  .nx-customers-grid .nx-customers-logo:nth-child(4){order:4}
  .nx-customers-grid .nx-customers-logo:nth-child(5){order:8}
  .nx-customers-grid .nx-customers-logo:nth-child(6){order:3}
  .nx-customers-grid .nx-customers-logo:nth-child(7){order:6}
  .nx-customers-grid .nx-customers-logo:nth-child(8){order:7}
  .nx-customers-grid .nx-customers-logo:nth-child(9){order:5}
  .nx-customers-logo img{height:26px !important;max-width:90px;width:auto;object-fit:contain}
  .nx-clients{padding:10px 0}
  .nx-clients-label{font-size:12px;text-align:center;margin-bottom:14px;display:block}
  .nx-hero-clients .nx-clients-mask{height:44px}
  .nx-hero-clients .nx-clients-track{height:44px;animation-duration:6s}
  .nx-clients-item{padding:0 20px;height:44px;justify-content:center}
  .nx-clients-img{height:32px;width:auto;max-height:32px;object-fit:contain;flex-shrink:0;display:block}

  /* ─ Product showcase mobile ─ */
  .nx-ghc-section{padding:16px 0 24px}
  .nx-ghc-h2{font-size:clamp(22px,6.5vw,28px);color:#1d1d1f}
  .nx-ghc-sub{color:#6e6e73}
  .nx-gs-tray{flex-direction:column;border-radius:8px;padding:24px 24px 0;gap:16px;margin-top:28px}
  .nx-gs-shot{border-radius:6px 6px 0 0}
  .nx-gs-shot img{height:340px}
  .nx-gs-caption{font-size:13px;margin-top:16px}

  /* ─ Social integrations carousel ─ */
  .nx-social-strip{padding:28px 0}
  .nx-social-item{padding:0 18px}
  .nx-social-icon{width:26px;height:26px}
  .nx-social-name{font-size:10px}
  .nx-social-track{animation-duration:4s}

  /* ─ Features ─ */
  .nx-features{padding:16px 0 16px}
  .nx-feat-grid{grid-template-columns:1fr 1fr;gap:8px}
  .nx-feat-card{padding:14px 12px;border-radius:8px}
  .nx-feat-badge{font-size:9px;padding:3px 9px;margin-bottom:8px}
  .nx-feat-title{font-size:13px;margin-bottom:6px}
  .nx-feat-body{display:none}
  .nx-feat-tags{display:none}
  .nx-feat-card-bottom{padding-top:10px;flex-direction:column;align-items:flex-start;gap:6px}
  .nx-feat-metric{font-size:20px;gap:4px}
  .nx-feat-metric-label{font-size:9px}
  .nx-feat-cta{font-size:10px;gap:4px}
  .nx-section-pre{margin-bottom:36px}
  .nx-section-h2{font-size:clamp(26px,7vw,36px)}

  /* ─ How it works ─ */
  .nx-howit{padding:24px 0 12px}
  .nx-howit-grid{grid-template-columns:1fr 1fr;gap:20px 14px}
  .nx-howit-step:nth-child(3){grid-column:1 / -1;text-align:left}
  .nx-howit-num{font-size:11px;margin-bottom:6px}
  .nx-howit-title{font-size:14px;margin-bottom:4px}
  .nx-howit-desc{font-size:11.5px;line-height:1.5}

  /* ─ Problem statement ─ */
  .nx-problem-section{padding:28px 0}
  .nx-problem-grid{grid-template-columns:repeat(2,1fr);gap:10px}
  .nx-problem-card{padding:14px 12px}
  .nx-problem-stat{font-size:21px;margin-bottom:5px}
  .nx-problem-desc{font-size:11px;line-height:1.45}
  .nx-problem-icon{margin-top:8px}
  .nx-problem-icon svg{width:15px;height:15px}
  .nx-problem-source{font-size:11px;margin-top:14px}

  /* ─ Lead capture + digital card (edge-to-edge, sin gutters) ─ */
  .nx-lc-section{padding:6px 0 20px}
  .nx-lc-section .nx-container{padding:0}
  .nx-lc-section .nx-section-pre{padding:0 18px;margin-bottom:20px}
  .nx-lc-photo{border-radius:0;margin-bottom:14px;box-shadow:none}
  .nx-lc-photo img{max-height:200px}
  /* This one isn't edge-to-edge like the rest of the section — matches the
     inset look of the Features section's photo (container-padding gap on
     both sides, no bleed) instead. */
  .nx-lc-photo--bottom{margin-left:18px;margin-right:18px}
  .nx-lc-photo--bottom img{max-height:140px}
  .nx-lc-card{border-radius:0;border-left:none;border-right:none;box-shadow:none}
  .nx-lc-tab{padding:9px 6px;font-size:10.5px}
  .nx-lc-grid{grid-template-columns:1fr}
  .nx-lc-item{display:flex;align-items:flex-start;gap:10px;text-align:left;padding:11px 16px;border-right:none;border-bottom:1px solid rgba(0,0,0,.06)}
  .nx-lc-item:last-child{border-bottom:none}
  .nx-lc-icon{width:26px;height:26px;border-radius:5px;margin-bottom:0;flex-shrink:0}
  .nx-lc-icon svg{width:12px;height:12px}
  .nx-lc-item-title{font-size:12px;margin-bottom:1px}
  .nx-lc-item-desc{font-size:10.5px;line-height:1.4;max-width:none;margin:0}

  /* ─ Security ─ */
  .nx-security{padding:16px 0 32px}
  .nx-security-grid{grid-template-columns:repeat(2,1fr);gap:20px 16px}
  .nx-security-icon{margin-bottom:8px}
  .nx-security-icon svg{width:16px;height:16px}
  .nx-security-title{font-size:13px;margin-bottom:4px}
  .nx-security-desc{font-size:11px;line-height:1.4}

  /* ─ Dark / capabilities ─ */
  .nx-dark{padding:56px 0}
  .nx-dark-grid{grid-template-columns:1fr;gap:28px}
  .nx-dark-h2{font-size:clamp(24px,7vw,34px)}
  .nx-dark-sub{font-size:14px}
  .nx-dark-img{border-radius:8px}

  /* ─ Showcase & phone ─ */
  .nx-showcase-h2{font-size:clamp(22px,6.5vw,30px)}
  .nx-showcase-body{font-size:14px}
  .nx-showcase-li{font-size:13px}

  /* ─ Plans ─ */
  .nx-plans{padding:32px 0 40px}
  .nx-plans .nx-section-pre{margin-bottom:16px}
  .nx-plans .nx-section-h2{font-size:22px}
  .nx-plans .nx-section-sub{font-size:12.5px;margin-bottom:0}
  .nx-plans .nx-billing-toggle{margin-top:10px}
  .nx-plans .nx-billing-opt{padding:6px 12px;font-size:11px;gap:4px}
  .nx-plans .nx-billing-save{font-size:9px;padding:1px 6px}
  .nx-plans-mobile-tabs{display:flex;align-items:center;gap:4px;background:rgba(0,0,0,0.05);border-radius:980px;padding:4px;margin:14px auto 0;max-width:420px}
  .nx-plans-mobile-tab{flex:1;background:none;border:none;border-radius:980px;padding:8px 6px;font-size:12px;font-weight:500;color:var(--muted);cursor:pointer;transition:all .2s;font-family:inherit;white-space:nowrap}
  .nx-plans-mobile-tab--on{background:#fff;color:var(--text);box-shadow:0 1px 4px rgba(0,0,0,0.12)}
  .nx-plan-tab-inactive{display:none}
  .nx-plans-grid{grid-template-columns:1fr;gap:14px;max-width:420px;margin:14px auto 0}
  .nx-plan{padding:22px 20px;border-radius:7px}
  .nx-plan-badge{font-size:10px;padding:3px 11px;margin-bottom:12px}
  .nx-plan-name{font-size:17px;margin-bottom:4px}
  .nx-plan-desc{display:block;font-size:12.5px;margin-bottom:14px}
  .nx-plan-price-wrap{margin:6px 0 14px;padding:14px 0}
  .nx-plan-price-old-row{margin-bottom:4px}
  .nx-plan-price-old{font-size:12px}
  .nx-plan-price-note{font-size:9px;padding:2px 7px}
  .nx-plan-price-main{font-size:34px}
  .nx-plan-price-sub{font-size:12px}
  .nx-plan-price-contact{min-height:0}
  .nx-plan-price-contact-main{font-size:19px}
  .nx-plan-price-contact-sub{font-size:11.5px;margin-top:4px}
  .nx-plan-list{gap:9px;margin-bottom:18px}
  .nx-plan-li{font-size:12.5px;gap:8px;line-height:1.4}
  .nx-plan-list .nx-li-check svg{width:13px;height:13px}
  .nx-plan-btn{height:44px;font-size:13.5px;border-radius:7px}

  /* ─ Footer ─ */
  .nx-footer{padding:10px 0 0}
  .nx-footer-grid{grid-template-columns:1fr 1fr;gap:8px 12px;padding-bottom:10px}
  .nx-footer-bottom{flex-wrap:wrap;justify-content:center;gap:6px 16px;text-align:center;padding:6px 0}
  .nx-footer-bottom>span{font-size:11px}

  /* ─ FAQ ─ */
  .nx-faq{padding:36px 0}
  .nx-faq-item{padding:10px 0}
  .nx-faq-q>span{font-size:13.5px}
  .nx-faq-icon{width:22px;height:22px}
  .nx-faq-a{font-size:13px;margin:6px 0 2px;max-width:100%}

  /* ─ Modal ─ */
  .qm-row{grid-template-columns:1fr}
  .qm-modal{border-radius:12px}
  .qm-head,.qm-body{padding:18px 20px}

  /* ─ Scroll reveal — fluido en mobile ─ */
  .nx-reveal{
    transition-duration:1.8s,1.8s,1.5s;
    transition-property:opacity,transform,filter;
    transition-timing-function:cubic-bezier(.16,1,.3,1),cubic-bezier(.16,1,.3,1),cubic-bezier(.16,1,.3,1);
    transform:translateY(16px);
    filter:blur(8px);
    -webkit-backface-visibility:hidden;
    backface-visibility:hidden;
    will-change:opacity,transform,filter;
  }
  .nx-reveal.nx-visible{transform:translateY(0);filter:blur(0)}
  .nx-reveal--left{transform:translateX(-16px);filter:blur(8px)}
  .nx-reveal--left.nx-visible{transform:translateX(0);filter:blur(0)}
  .nx-reveal--right{transform:translateX(16px);filter:blur(8px)}
  .nx-reveal--right.nx-visible{transform:translateX(0);filter:blur(0)}
}
/* ── ELEVATE CONTENT ── */
.nx-hero,.nx-features,.nx-problem-section,.nx-lc-section,.nx-api-section,.nx-bento-section,.nx-fan-section,.nx-globe-section,.nx-plans,.nx-faq,.nx-footer{position:relative;z-index:1}

/* ── ENHANCED PAGE TRANSITIONS ── */
/* Nav link underline slide */
.nx-nav-link{position:relative}
.nx-nav-link::after{content:'';position:absolute;bottom:-3px;left:0;width:0;height:1.5px;background:var(--text);transition:width .25s cubic-bezier(.16,1,.3,1)}
.nx-nav-link:hover::after{width:100%}


/* Plan cards — hover */
.nx-plan:hover{box-shadow:0 12px 40px rgba(0,0,0,.1),0 4px 14px rgba(0,0,0,.07);transform:translateY(-4px)}
.nx-plan--featured:hover{box-shadow:0 32px 80px rgba(0,0,0,.32),0 4px 20px rgba(0,0,0,.16)}

/* Step cards — subtle blue on hover */

/* Tag pills — blue on hover */
.nx-tag{transition:background .2s,transform .2s}
.nx-tag:hover{background:rgba(37,99,235,1);transform:scale(1.08)}

/* Capability tags — blue glow on hover */
.nx-cap-tag:hover{background:rgba(59,130,246,.2);border-color:rgba(96,165,250,.5);color:#fff;box-shadow:0 0 24px rgba(59,130,246,.18)}

/* Dashboard & phone showcase shadows */
.nx-showcase-visual{transition:box-shadow .4s ease,transform .4s ease}
.nx-showcase-visual:hover{box-shadow:0 20px 60px rgba(59,130,246,.15),0 4px 20px rgba(0,0,0,.08);transform:translateY(-4px) scale(1.005)}

/* Buttons — subtle blue shadow on hover */
.nx-btn-pill:hover{box-shadow:0 6px 20px rgba(59,130,246,.25)}
.nx-btn-pill--white:hover{box-shadow:0 6px 20px rgba(255,255,255,.2)}

/* Plan button */
.nx-btn-outline:hover{box-shadow:0 4px 14px rgba(59,130,246,.12)}

/* Dashboard showcase — copy is supporting, visual leads */
.nx-showcase-copy .nx-showcase-h2{font-size:clamp(20px,2.2vw,28px)}
.nx-showcase-copy .nx-showcase-body{font-size:14px;line-height:1.65}

/* Hero page-load entrance */
.nx-load{animation:nxLoadIn .95s cubic-bezier(.22,1,.36,1) both}

/* Nav slide-down entrance */
.nx-nav{animation:nxNavIn .5s cubic-bezier(.22,1,.36,1) both}

/* Button active/pressed feedback */
.nx-btn-pill{transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s}
.nx-btn-pill:hover{transform:translateY(-2px)}
.nx-btn-pill:active{transform:scale(.94) translateY(0) !important;box-shadow:none !important;opacity:1}
.nx-btn-outline{transition:all .2s cubic-bezier(.22,1,.36,1)}
.nx-btn-outline:active{transform:scale(.95)}
.nx-btn-ghost{transition:color .15s,transform .2s}
.nx-btn-ghost:active{transform:scale(.93)}

/* Plan button active */
.nx-plan-btn:active{transform:scale(.95) !important}

/* Demo button — diagonal glint sweep on hover */
@keyframes demo-shimmer{0%{left:-110%}60%,100%{left:165%}}
.nx-btn-demo{position:relative;overflow:hidden}
.nx-btn-demo::before{content:'';position:absolute;top:0;left:-110%;width:55%;height:100%;background:linear-gradient(118deg,transparent 0%,rgba(255,255,255,.08) 25%,rgba(255,255,255,.38) 50%,rgba(255,255,255,.08) 75%,transparent 100%);transform:skewX(-18deg);pointer-events:none;animation:demo-shimmer 1.6s cubic-bezier(.22,1,.36,1) infinite}
@media(prefers-reduced-motion:reduce){.nx-btn-demo::before{display:none}}

@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default Homepage;
